import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  ArrowLeft, Loader2, Save, Plus, Trash2, ChevronRight, ChevronDown,
  Sparkles, Maximize2, Minimize2, GripVertical, FolderPlus, Download, Pencil,
  AlertTriangle, Table2, X, Star, ChevronsDownUp, ChevronsUpDown, Paintbrush,
  Copy as CopyIcon,
} from 'lucide-react'
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors,
  useDraggable, useDroppable, closestCorners,
  type DragStartEvent, type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { EmbeddedDesignerProps } from './embeddedDesigner'
import {
  loadVisualSchema, saveVisualSchema,
} from '@/api/designers'
import api from '@/api/client'
import ConnectionPicker from '@/components/connections/ConnectionPicker'
import {
  VariableInsert, RoleAdder, useSqlVariableTokens, useFunctionalRoles,
} from '@/components/common/PolicyPickers'
import { parseSimpleJoin, tableColumns } from './visual/joinHelpers'
import { fetchTableColumns, listSourceTables, prettify, uniqueColumnName } from './visual/tableSource'
import JoinsEditor from './joins/JoinsEditor'
import { useProjectStore } from '@/store/projectStore'
import { useBuilderContextStore } from '@/store/builderContextStore'
import { useAgentDeletion, useAgentRefresh } from '@/hooks/useAgentRefresh'
import { useDesignHistory } from '@/hooks/useDesignHistory'
import UndoRedoButtons from '@/components/common/UndoRedoButtons'
import { useProjectPermissions } from '@/auth/useProjectPermissions'
import { ReadOnlyBadge } from '@/auth/ReadOnlyBadge'
import { useRunOnlyRedirect } from '@/auth/useRunOnlyRedirect'
import type {
  VisualSchema, VisualSchemaFile, SchemaTable, SchemaTableColumn,
  SchemaJoin, SchemaRowSecurityPolicy, SchemaColumnSecurityPolicy,
  SchemaColumnGroup, SchemaGroupColumn,
} from './visual/types'
import DisplayFormatEditor from './transaction/DisplayFormatEditor'
import type { ColumnDef, DisplayFormat } from '@twasta/page-runtime/txn'

const COLTYPE_OPTIONS = ['CHAR', 'NUMBER', 'DATE'] as const
const JOIN_TYPE_OPTIONS = ['INNER', 'LEFT', 'RIGHT', 'FULL'] as const
const FUNCTION_OPTIONS = ['', 'SUM', 'COUNT', 'AVG', 'MIN', 'MAX'] as const
const COL_SEC_ACTIONS = ['HIDE', 'MASK'] as const
const ALLOWED_TYPE_OPTIONS = ['ANY', 'NUMBER', 'STRING', 'DATE'] as const
type ViewTab = 'model' | 'joins' | 'security' | 'settings'

const MONO_FONT = "var(--font-mono, 'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace)"
// Object-type color for M (Data Model) — same map the workbench legend uses.
const M_CHIP_COLOR = '#a371f7'

// ── factory helpers ───────────────────────────────────────────────
function defaultGroups(): SchemaColumnGroup[] {
  return [
    { GROUP_ID: 'G001', GROUP_NAME: 'Facts', GROUP_ICON: '📊', ALLOWED_TYPES: 'NUMBER', DESCRIPTION: 'Measures and numeric values', COLUMNS: [] },
    { GROUP_ID: 'G002', GROUP_NAME: 'Dimensions', GROUP_ICON: '🏷️', ALLOWED_TYPES: 'STRING', DESCRIPTION: 'Descriptive attributes to group / slice by', COLUMNS: [] },
  ]
}

function blankSchema(name: string): VisualSchema {
  return {
    SCHEMA_NAME: name,
    SCHEMA_DESCR: name.replace(/_/g, ' '),
    SCHEMA_TYPE: 'S',
    CONNECTION_ID: '',
    MAIN_TABLE: '',
    TABLES: [],
    JOINS: [],
    ROW_SECURITY: [],
    COLUMN_SECURITY: [],
    COLUMN_GROUPS: defaultGroups(),
  }
}

function blankColumn(): SchemaTableColumn {
  return {
    DBNAME: '',
    DISPLAY_NAME: '',
    COLTYPE: 'CHAR',
    DBSIZE: 100,
    NATIVETYPE: 'AN',
    JAVATYPE: 'java.lang.String',
    DEFAULTFUNCTION: '',
    ROW_SECURITY: false,
    COL_SECURITY: false,
    HIDDEN: false,
  }
}

function blankTable(): SchemaTable {
  return { TABLE_NAME: '', TABLE_DISPLAY_NAME: '', ALIAS: '', CONDITION: '', COLUMNS: [] }
}

function blankJoin(n: number): SchemaJoin {
  return {
    JOIN_ID: `J${String(n).padStart(3, '0')}`,
    LEFT_TABLE: '', RIGHT_TABLE: '', JOIN_TYPE: 'LEFT', JOIN_CONDITION: '',
  }
}

function blankRowSec(n: number): SchemaRowSecurityPolicy {
  return { POLICY_ID: `RLS_${String(n).padStart(3, '0')}`, TABLE_NAME: '', CONDITION: '', APPLIES_TO_ROLES: [] }
}

function blankColSec(n: number): SchemaColumnSecurityPolicy {
  return { POLICY_ID: `CLS_${String(n).padStart(3, '0')}`, TABLE_NAME: '', COLUMN_NAME: '', ACTION: 'HIDE', APPLIES_TO_ROLES: [] }
}

function nextGroupId(groups: SchemaColumnGroup[]): string {
  let max = 0
  for (const g of groups) {
    const m = /^G(\d+)$/.exec(g.GROUP_ID || '')
    if (m) max = Math.max(max, parseInt(m[1], 10))
  }
  return `G${String(max + 1).padStart(3, '0')}`
}

// Advisory: does a column type fit a folder's allowed type?
function typeFitsFolder(coltype: string | undefined, allowed: string | undefined): boolean {
  const a = allowed || 'ANY'
  if (a === 'ANY') return true
  const c = (coltype || 'CHAR').toUpperCase()
  if (a === 'NUMBER') return c === 'NUMBER'
  if (a === 'DATE') return c === 'DATE'
  if (a === 'STRING') return c === 'CHAR'
  return true
}

function colKey(c: { TABLE_NAME?: string; DBNAME?: string }): string {
  return `${c.TABLE_NAME || ''}.${c.DBNAME || ''}`
}

// Friendly labels for the read-only property chips shown under a source column.
const COL_META_LABELS: Record<string, string> = {
  COLTYPE: 'type', DBSIZE: 'width', size: 'width', NATIVETYPE: 'native',
  DEFAULT_VALUE: 'default', DEFAULT: 'default', default: 'default', column_default: 'default',
  NULLABLE: 'nullable', nullable: 'nullable', mandatory: 'required', required: 'required',
  KEY: 'key', key: 'key', sequence: 'seq', PRECISION: 'precision', SCALE: 'scale',
  data_type: 'db type', comment: 'note', description: 'note', allowed_values: 'values',
}
// Internal bookkeeping fields never surfaced as user-facing properties.
const COL_META_HIDE = new Set([
  'DBNAME', 'DISPLAY_NAME', 'DEFAULTFUNCTION', 'ROW_SECURITY', 'COL_SECURITY',
  'HIDDEN', 'JAVATYPE', 'UNIQUE_NAME',
])

// The read-only property chips for a physical source column: data type, width,
// native type, default value, and any other extended property the source carries.
function sourceColMeta(col: SchemaTableColumn): { label: string; value: string }[] {
  const out: { label: string; value: string }[] = []
  const seen = new Set<string>()
  const add = (label: string, v: unknown) => {
    if (v === undefined || v === null || v === '') return
    let val: string
    if (typeof v === 'boolean') val = v ? 'yes' : 'no'
    else if (Array.isArray(v)) { if (!v.length) return; val = v.map(String).join(', ') }
    else if (typeof v === 'object') return
    else val = String(v)
    if (!val || seen.has(`${label}=${val}`)) return
    seen.add(`${label}=${val}`)
    out.push({ label, value: val })
  }
  // Preferred order first, then any remaining extended properties.
  add('type', col.COLTYPE)
  add('width', col.DBSIZE)
  add('native', col.NATIVETYPE && col.NATIVETYPE !== 'AN' ? col.NATIVETYPE : undefined)
  for (const k of Object.keys(col)) {
    if (COL_META_HIDE.has(k) || k === 'COLTYPE' || k === 'DBSIZE' || k === 'NATIVETYPE') continue
    add(COL_META_LABELS[k] || prettify(k), (col as Record<string, unknown>)[k])
  }
  return out
}

// Plain-English reading of a join, derived purely from its current state.
function joinPlainTerms(j: SchemaJoin): string {
  if (!j.LEFT_TABLE || !j.RIGHT_TABLE) return 'pick both tables to define the join'
  if (!j.JOIN_CONDITION) return 'no ON condition yet — pick the key columns above, or type it'
  const parsed = parseSimpleJoin(j.JOIN_CONDITION, j.LEFT_TABLE, j.RIGHT_TABLE)
  if (!parsed.lc || !parsed.rc) return 'custom expression — written by hand'
  const rightNoun = j.RIGHT_TABLE.toLowerCase().replace(/^dim_/, '').replace(/_/g, ' ')
  return `each ${j.LEFT_TABLE} row is matched to its ${rightNoun}`
}

// Plain-English reading of a row-security policy.
function rlsPlainTerms(p: SchemaRowSecurityPolicy): string {
  if (!p.CONDITION) return 'No predicate yet — this policy does nothing.'
  const roles = (p.APPLIES_TO_ROLES || []).length ? (p.APPLIES_TO_ROLES || []).join(', ') : 'everyone'
  const table = p.TABLE_NAME && p.TABLE_NAME !== 'ALL' ? p.TABLE_NAME : 'any queried table'
  return `${roles} only see ${table} rows where ${p.CONDITION}`
}

// Hardened loader: defaults EVERY nested field so a malformed schema (e.g. a
// row-security policy missing APPLIES_TO_ROLES) can never crash the renderer.
function sanitizeSchema(raw: unknown, schemaName: string): VisualSchema {
  if (!raw || typeof raw !== 'object') return blankSchema(schemaName)
  const file = raw as Record<string, unknown>
  const inner = (file.SCHEMA as Record<string, unknown>) || file
  const out: VisualSchema = {
    SCHEMA_NAME: schemaName, // file name is authoritative
    SCHEMA_DESCR: String(inner.SCHEMA_DESCR || schemaName.replace(/_/g, ' ')),
    SCHEMA_TYPE: (inner.SCHEMA_TYPE as string) || 'S',
    CONNECTION_ID: (inner.CONNECTION_ID as string) || '',
    MAIN_TABLE: (inner.MAIN_TABLE as string) || '',
    TABLES: Array.isArray(inner.TABLES) ? (inner.TABLES as SchemaTable[]) : [],
    JOINS: Array.isArray(inner.JOINS) ? (inner.JOINS as SchemaJoin[]) : [],
    JOIN_LAYOUT: (inner.JOIN_LAYOUT && typeof inner.JOIN_LAYOUT === 'object')
      ? (inner.JOIN_LAYOUT as VisualSchema['JOIN_LAYOUT']) : undefined,
    ROW_SECURITY: Array.isArray(inner.ROW_SECURITY) ? (inner.ROW_SECURITY as SchemaRowSecurityPolicy[]) : [],
    COLUMN_SECURITY: Array.isArray(inner.COLUMN_SECURITY) ? (inner.COLUMN_SECURITY as SchemaColumnSecurityPolicy[]) : [],
    COLUMN_GROUPS: Array.isArray(inner.COLUMN_GROUPS) ? (inner.COLUMN_GROUPS as SchemaColumnGroup[]) : [],
  }
  out.TABLES = out.TABLES.map((t) => ({
    TABLE_NAME: t.TABLE_NAME || '',
    TABLE_DISPLAY_NAME: t.TABLE_DISPLAY_NAME || '',
    ALIAS: t.ALIAS || '',
    CONDITION: (t.CONDITION as string) || '',
    COLUMNS: Array.isArray(t.COLUMNS) ? t.COLUMNS.map((c) => ({ ...blankColumn(), ...c })) : [],
  }))
  // Crash fix — every policy gets a guaranteed APPLIES_TO_ROLES array.
  out.ROW_SECURITY = (out.ROW_SECURITY || []).map((p, i) => ({
    POLICY_ID: p?.POLICY_ID || `RLS_${String(i + 1).padStart(3, '0')}`,
    TABLE_NAME: p?.TABLE_NAME || '',
    CONDITION: p?.CONDITION || '',
    APPLIES_TO_ROLES: Array.isArray(p?.APPLIES_TO_ROLES) ? p.APPLIES_TO_ROLES : [],
  }))
  out.COLUMN_SECURITY = (out.COLUMN_SECURITY || []).map((p, i) => ({
    POLICY_ID: p?.POLICY_ID || `CLS_${String(i + 1).padStart(3, '0')}`,
    TABLE_NAME: p?.TABLE_NAME || '',
    COLUMN_NAME: p?.COLUMN_NAME || '',
    ACTION: p?.ACTION || 'HIDE',
    APPLIES_TO_ROLES: Array.isArray(p?.APPLIES_TO_ROLES) ? p.APPLIES_TO_ROLES : [],
  }))
  out.COLUMN_GROUPS = (out.COLUMN_GROUPS || []).map((g, i) => ({
    GROUP_ID: g?.GROUP_ID || `G${String(i + 1).padStart(3, '0')}`,
    GROUP_NAME: g?.GROUP_NAME || `Folder ${i + 1}`,
    GROUP_DISPLAY_NAME: g?.GROUP_DISPLAY_NAME || '',
    GROUP_ICON: g?.GROUP_ICON || '📁',
    ALLOWED_TYPES: (g?.ALLOWED_TYPES as SchemaColumnGroup['ALLOWED_TYPES']) || 'ANY',
    DESCRIPTION: g?.DESCRIPTION || '',
    COLUMNS: Array.isArray(g?.COLUMNS)
      ? g.COLUMNS.map((c) => ({
          TABLE_NAME: c?.TABLE_NAME || '',
          DBNAME: c?.DBNAME || '',
          DISPLAY_NAME: c?.DISPLAY_NAME || prettify(c?.DBNAME || ''),
          COLTYPE: c?.COLTYPE || 'CHAR',
          DEFAULTFUNCTION: c?.DEFAULTFUNCTION || '',
          DESCRIPTION: c?.DESCRIPTION || '',
          // Preserve the rich display format the author set (dropped otherwise,
          // since this reconstructs the object field-by-field).
          ...(c?.display ? { display: c.display as DisplayFormat } : {}),
        }))
      : [],
  }))
  if ((out.COLUMN_GROUPS || []).length === 0) out.COLUMN_GROUPS = defaultGroups()
  return out
}

// drag-payload discriminated union carried on dnd-kit data
// A single column reference carried while dragging (source → folder / table).
type DragCol = { tableName: string; dbname: string; coltype: string; label: string }
// One or more source columns (multi-select drags carry the whole selection).
type SourceDrag = { kind: 'source'; cols: DragCol[] }
// A whole source table dragged onto the right → becomes its own folder.
type TableDrag = { kind: 'table'; tableName: string; cols: DragCol[] }
type PlacedDrag = { kind: 'placed'; groupId: string; tableName: string; dbname: string; coltype: string; label: string }
type FolderDrop = { kind: 'folder'; groupId: string }
// A whole folder dragged by its header grip → reorder folders.
type FolderSortDrag = { kind: 'foldersort'; groupId: string; name: string; icon: string; count: number }
// The right-panel canvas (not a specific folder) — dropping here makes a folder.
type NewFolderDrop = { kind: 'newfolder' }
type ActiveDrag = SourceDrag | TableDrag | PlacedDrag | FolderSortDrag
type OverDrop = FolderDrop | PlacedDrag | NewFolderDrop | FolderSortDrag
const colSelKey = (tableName: string, dbname: string) => `${tableName || ''}.${dbname || ''}`

export default function SchemaDesignerPage({
  embedded = false,
  hostFullscreen = false,
  embedProject,
  embedObjName,
  onDirtyChange,
  registerSave,
  onSaved,
}: EmbeddedDesignerProps = {}) {
  const params = useParams<{ project: string; schemaName: string }>()
  const project = embedProject ?? params.project
  useRunOnlyRedirect(project)
  const schemaName = embedObjName ?? params.schemaName
  const navigate = useNavigate()
  const location = useLocation()
  const projectName = project || ''
  const sName = schemaName || ''
  const { currentProject, selectProject } = useProjectStore()

  useEffect(() => {
    if (project && currentProject?.application.folder_name !== project) {
      selectProject(project)
    }
  }, [project, currentProject, selectProject])

  const [schema, setSchema] = useState<VisualSchema>(() => blankSchema(sName))
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const { canEditObjectsOrLoading: canEdit } = useProjectPermissions()
  const [generating, setGenerating] = useState(false)
  const [aiOpen, setAiOpen] = useState(false)
  const [aiPrompt, setAiPrompt] = useState('')
  const [view, setView] = useState<ViewTab>('model')
  const [expandedTables, setExpandedTables] = useState<Set<string>>(new Set())
  const [editingGroupCol, setEditingGroupCol] = useState<Set<string>>(new Set()) // placed columns being edited
  const [selectedCols, setSelectedCols] = useState<Set<string>>(new Set()) // multi-select source columns (TABLE.COL keys)
  const [importOpen, setImportOpen] = useState(false)
  const [activeDrag, setActiveDrag] = useState<ActiveDrag | null>(null)
  // Snapshot of the last-saved schema for unsaved-change detection.
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null)
  const isDirty = savedSnapshot !== null && JSON.stringify(schema) !== savedSnapshot
  const history = useDesignHistory(schema, setSchema, { disabled: !canEdit })
  const { reset: resetHistory } = history
  const [embedFullscreen, setEmbedFullscreen] = useState(false)
  const varTokens = useSqlVariableTokens(projectName)
  const funcRoles = useFunctionalRoles(projectName)
  // Join-builder column picks awaiting their other side (keyed by join index).
  const [pendingJoinCols, setPendingJoinCols] = useState<Record<number, { lc: string; rc: string }>>({})

  const agentRefresh = useAgentRefresh(projectName, isDirty)

  // An agent run that DELETED this object must close it, not refetch it — a
  // refetch 404s and leaves the removed object on screen (and a later Save
  // would resurrect it). Deletions only became visible to the client in Phase
  // 0.8 of docs/ssh_agent_console_support_plan.md.
  useAgentDeletion(projectName, sName, 'visual_schemas', () => {
    if (embedded) return   // the host tab owns navigation
    navigate('/workbench/analytics')
  })

  // Publish the open schema as the Builder/Ask-AI "open object" so the agent
  // resolves "this schema" / "this" / "here" to it instead of guessing — parity
  // with the nav tree and workflow designer.
  useEffect(() => {
    if (!sName) return
    useBuilderContextStore.getState().setOpenObject({
      obj_name: sName,
      obj_type: 'M',
      title: schema?.SCHEMA_NAME || sName,
    })
    return () => useBuilderContextStore.getState().setOpenObject(null)
  }, [sName, schema?.SCHEMA_NAME])

  useEffect(() => {
    let cancelled = false
    if (!projectName || !sName) return
    setLoading(true)
    loadVisualSchema(projectName, sName)
      .then((data) => {
        if (cancelled) return
        const next = data && data.content
          ? sanitizeSchema(data.content, sName)
          : blankSchema(sName)
        setSchema(next)
        setSavedSnapshot(JSON.stringify(next))
        // A (re)load replaces the schema wholesale — stale undo entries would
        // restore a different/older schema.
        resetHistory()
      })
      .catch((err) => {
        console.error('Failed to load schema:', err)
        toast.error('Failed to load schema')
        const fallback = blankSchema(sName)
        setSchema(fallback)
        setSavedSnapshot(JSON.stringify(fallback))
        resetHistory()
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [projectName, sName, agentRefresh, resetHistory])

  const update = useCallback((mutator: (s: VisualSchema) => void) => {
    setSchema((prev) => {
      const next = JSON.parse(JSON.stringify(prev)) as VisualSchema
      mutator(next)
      return next
    })
  }, [])

  const toggleTable = (name: string) => {
    setExpandedTables((prev) => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name); else next.add(name)
      return next
    })
  }
  // Keys of every table (matches the render key: TABLE_NAME || __t<i>).
  const allTableKeys = useCallback(
    (s: VisualSchema) => s.TABLES.map((t, i) => t.TABLE_NAME || `__t${i}`),
    [],
  )
  const expandAll = () => setExpandedTables(new Set(allTableKeys(schema)))
  const collapseAll = () => setExpandedTables(new Set())
  const toggleSelectCol = (key: string, additive: boolean) => {
    setSelectedCols((prev) => {
      const next = additive ? new Set(prev) : new Set<string>()
      if (prev.has(key)) next.delete(key); else next.add(key)
      return next
    })
  }
  const toggleEditGroupCol = (key: string) => {
    setEditingGroupCol((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key); else next.add(key)
      return next
    })
  }

  const handleSave = useCallback(async () => {
    if (!projectName || !sName) return
    setSaving(true)
    try {
      const file: VisualSchemaFile = { SCHEMA: schema }
      await saveVisualSchema(projectName, sName, file)
      setSavedSnapshot(JSON.stringify(schema))
      toast.success('Schema saved')
      // The list card's table count is derived server-side — tell the shell.
      onSaved?.()
    } catch (err) {
      console.error('Save failed', err)
      toast.error('Failed to save schema')
    } finally {
      setSaving(false)
    }
  }, [projectName, sName, schema, onSaved])

  useEffect(() => {
    if (!embedded || !onDirtyChange) return
    onDirtyChange(isDirty)
    return () => onDirtyChange(false)
  }, [embedded, isDirty, onDirtyChange])
  useEffect(() => {
    if (!embedded || !registerSave) return
    registerSave(async () => { await handleSave(); return true })
  }, [embedded, registerSave, handleSave])

  const handleGenerate = useCallback(async () => {
    if (!projectName || !sName || !aiPrompt.trim()) return
    setGenerating(true)
    try {
      const res = await api.post('/designers/generate-visual-schema', {
        project: projectName,
        schema_name: sName,
        description: aiPrompt.trim(),
        // Ground the generator on the schema's bound connection (if any) so it
        // reads the real tables/columns from that source, not the local design.
        connection_id: (schema.CONNECTION_ID || '').trim() || undefined,
      })
      if (res.data?.content) {
        setSchema(sanitizeSchema(res.data.content, sName))
      }
      setAiOpen(false)
      toast.success('Schema generated')
    } catch (err) {
      console.error('Generate failed', err)
      toast.error('Failed to generate schema')
    } finally {
      setGenerating(false)
    }
  }, [projectName, sName, aiPrompt, schema.CONNECTION_ID])

  // ── group / folder mutators ──────────────────────────────────────
  const groups = schema.COLUMN_GROUPS || []

  // Which folders each source column has been placed in (TABLE.COL → [names]).
  // Drives the "already added" indicator on both the source tree and placed
  // chips; a column may legitimately live in several folders.
  // Every query alias in the dataset → the columns claiming it. Two columns
  // sharing an alias would collide in the SELECT (and, on Oracle, fail the
  // whole query with ORA-00918), so a clash is surfaced where it is edited.
  const aliasIndex = useMemo(() => {
    const m = new Map<string, string[]>()
    for (const t of schema.TABLES) {
      for (const c of t.COLUMNS) {
        const a = String(c.UNIQUE_NAME || '').trim().toUpperCase()
        if (!a) continue
        m.set(a, [...(m.get(a) || []), `${t.TABLE_NAME}.${c.DBNAME}`])
      }
    }
    return m
  }, [schema.TABLES])

  const placedIndex = useMemo(() => {
    const m = new Map<string, string[]>()
    for (const g of groups) {
      for (const c of g.COLUMNS) {
        const k = colSelKey(c.TABLE_NAME, c.DBNAME)
        const arr = m.get(k) || []
        arr.push(g.GROUP_NAME || 'Folder')
        m.set(k, arr)
      }
    }
    return m
  }, [groups])

  // The full multi-selection resolved to draggable column payloads, so dragging
  // any selected column carries the whole set into the folder.
  const selectedDragCols = useMemo<DragCol[]>(() => {
    const out: DragCol[] = []
    for (const t of schema.TABLES) {
      for (const c of t.COLUMNS) {
        const k = colSelKey(t.TABLE_NAME, c.DBNAME)
        if (selectedCols.has(k)) {
          out.push({
            tableName: t.TABLE_NAME, dbname: c.DBNAME,
            coltype: (c.COLTYPE as string) || 'CHAR',
            label: c.DISPLAY_NAME || prettify(c.DBNAME),
          })
        }
      }
    }
    return out
  }, [schema.TABLES, selectedCols])

  const findSourceCol = useCallback((s: VisualSchema, tn: string, dn: string): SchemaGroupColumn | null => {
    for (const t of s.TABLES) {
      if (t.TABLE_NAME !== tn) continue
      for (const c of t.COLUMNS) {
        if (c.DBNAME === dn) {
          return {
            TABLE_NAME: tn,
            DBNAME: dn,
            // Copy of the source column's query alias (absent on columns
            // imported before UNIQUE_NAME existed — those keep aliasing by
            // DBNAME, so old schemas and their visuals are untouched).
            ...(c.UNIQUE_NAME ? { UNIQUE_NAME: String(c.UNIQUE_NAME) } : {}),
            DISPLAY_NAME: c.DISPLAY_NAME || prettify(dn),
            COLTYPE: (c.COLTYPE as SchemaGroupColumn['COLTYPE']) || 'CHAR',
            DEFAULTFUNCTION: c.DEFAULTFUNCTION || '',
            DESCRIPTION: '',
          }
        }
      }
    }
    return null
  }, [])

  // Resolve a DragCol to a full placed-column object (inherits the source
  // column's coltype / default aggregation; carries no format yet — physical).
  const dragColToPlaced = useCallback((s: VisualSchema, dc: DragCol): SchemaGroupColumn => {
    return findSourceCol(s, dc.tableName, dc.dbname) || {
      TABLE_NAME: dc.tableName, DBNAME: dc.dbname,
      DISPLAY_NAME: dc.label || prettify(dc.dbname),
      COLTYPE: (dc.coltype as SchemaGroupColumn['COLTYPE']) || 'CHAR',
      DEFAULTFUNCTION: '', DESCRIPTION: '',
    }
  }, [findSourceCol])

  // Single entry-point for all drop resolution: whole-table → new folder,
  // one-or-many source columns → target/new folder, placed → reorder/move.
  const handleDrop = useCallback((a: ActiveDrag, o: OverDrop) => {
    if (a.kind === 'foldersort') return // folder reorder is resolved in onDragEnd
    update((s) => {
      const gs = s.COLUMN_GROUPS || (s.COLUMN_GROUPS = [])

      // A whole table always becomes its own new folder (dedup within it).
      if (a.kind === 'table') {
        const gid = nextGroupId(gs)
        const folder: SchemaColumnGroup = {
          GROUP_ID: gid, GROUP_NAME: prettify(a.tableName) || 'New Folder',
          GROUP_ICON: '🗂️', ALLOWED_TYPES: 'ANY', DESCRIPTION: '', COLUMNS: [],
        }
        for (const dc of a.cols) {
          if (folder.COLUMNS.some((c) => c.TABLE_NAME === dc.tableName && c.DBNAME === dc.dbname)) continue
          folder.COLUMNS.push(dragColToPlaced(s, dc))
        }
        gs.push(folder)
        return
      }

      if (a.kind === 'source') {
        // Dropping on the canvas (not a folder) creates a new folder for them.
        let to: SchemaColumnGroup | undefined
        if (o.kind === 'newfolder') {
          const gid = nextGroupId(gs)
          to = { GROUP_ID: gid, GROUP_NAME: 'New Folder', GROUP_ICON: '📁', ALLOWED_TYPES: 'ANY', DESCRIPTION: '', COLUMNS: [] }
          gs.push(to)
        } else {
          to = gs.find((g) => g.GROUP_ID === o.groupId)
        }
        if (!to) return
        let overIdx = o.kind === 'placed'
          ? to.COLUMNS.findIndex((c) => c.TABLE_NAME === o.tableName && c.DBNAME === o.dbname)
          : to.COLUMNS.length
        if (overIdx < 0) overIdx = to.COLUMNS.length
        for (const dc of a.cols) {
          // No duplicate WITHIN a folder (duplicates across folders are allowed).
          if (to.COLUMNS.some((c) => c.TABLE_NAME === dc.tableName && c.DBNAME === dc.dbname)) continue
          to.COLUMNS.splice(overIdx, 0, dragColToPlaced(s, dc))
          overIdx += 1
        }
        return
      }

      // placed → reorder within / move across folders. Dropping onto the bare
      // canvas is a no-op (nowhere meaningful to move a placed column to).
      // (Re-narrow inside the closure — the early foldersort return above
      // doesn't carry into this callback for the type checker.)
      if (a.kind !== 'placed') return
      if (o.kind === 'newfolder') return
      const targetGid = o.groupId
      const to = gs.find((g) => g.GROUP_ID === targetGid)
      if (!to) return
      const overIdx = o.kind === 'placed'
        ? to.COLUMNS.findIndex((c) => c.TABLE_NAME === o.tableName && c.DBNAME === o.dbname)
        : to.COLUMNS.length
      const from = gs.find((g) => g.GROUP_ID === a.groupId)
      if (!from) return
      const ci = from.COLUMNS.findIndex((c) => c.TABLE_NAME === a.tableName && c.DBNAME === a.dbname)
      if (ci < 0) return
      if (from === to) {
        const tgt = overIdx < 0 ? to.COLUMNS.length - 1 : overIdx
        to.COLUMNS = arrayMove(to.COLUMNS, ci, tgt)
      } else {
        if (to.COLUMNS.some((c) => c.TABLE_NAME === a.tableName && c.DBNAME === a.dbname)) {
          from.COLUMNS.splice(ci, 1) // already present in target — just remove the dup source
          return
        }
        const [col] = from.COLUMNS.splice(ci, 1)
        to.COLUMNS.splice(overIdx < 0 ? to.COLUMNS.length : overIdx, 0, col)
      }
    })
  }, [update, dragColToPlaced])

  const onDragStart = (e: DragStartEvent) => {
    if (!canEdit) return
    const d = e.active.data.current as ActiveDrag | undefined
    if (d && (d.kind === 'source' || d.kind === 'placed' || d.kind === 'table' || d.kind === 'foldersort')) setActiveDrag(d)
  }
  const onDragEnd = (e: DragEndEvent) => {
    setActiveDrag(null)
    if (!canEdit) return
    const a = e.active.data.current as ActiveDrag | undefined
    const o = e.over?.data.current as OverDrop | undefined
    if (!a || !o) return
    if (o.kind !== 'folder' && o.kind !== 'placed' && o.kind !== 'newfolder' && o.kind !== 'foldersort') return
    // Folder reorder — dropped onto (or inside) another folder → take its slot.
    if (a.kind === 'foldersort') {
      if (o.kind === 'newfolder') return
      const targetGid = o.groupId
      if (!targetGid || targetGid === a.groupId) return
      update((s) => {
        const gs = s.COLUMN_GROUPS || []
        const from = gs.findIndex((g) => g.GROUP_ID === a.groupId)
        const to = gs.findIndex((g) => g.GROUP_ID === targetGid)
        if (from >= 0 && to >= 0) s.COLUMN_GROUPS = arrayMove(gs, from, to)
      })
      return
    }
    handleDrop(a, o)
    // A multi-column drop consumes the selection.
    if (a.kind === 'source' && a.cols.length > 1) setSelectedCols(new Set())
  }

  const addGroup = () => update((s) => {
    const gs = s.COLUMN_GROUPS || (s.COLUMN_GROUPS = [])
    gs.push({ GROUP_ID: nextGroupId(gs), GROUP_NAME: 'New Folder', GROUP_ICON: '📁', ALLOWED_TYPES: 'ANY', DESCRIPTION: '', COLUMNS: [] })
  })
  const removeGroup = (gid: string) => update((s) => {
    s.COLUMN_GROUPS = (s.COLUMN_GROUPS || []).filter((g) => g.GROUP_ID !== gid)
  })
  const patchGroup = (gid: string, patch: Partial<SchemaColumnGroup>) => update((s) => {
    const g = (s.COLUMN_GROUPS || []).find((x) => x.GROUP_ID === gid)
    if (g) Object.assign(g, patch)
  })
  const removeGroupCol = (gid: string, tn: string, dn: string) => update((s) => {
    const g = (s.COLUMN_GROUPS || []).find((x) => x.GROUP_ID === gid)
    if (g) g.COLUMNS = g.COLUMNS.filter((c) => !(c.TABLE_NAME === tn && c.DBNAME === dn))
  })
  const patchGroupCol = (gid: string, tn: string, dn: string, patch: Partial<SchemaGroupColumn>) => update((s) => {
    const g = (s.COLUMN_GROUPS || []).find((x) => x.GROUP_ID === gid)
    const c = g?.COLUMNS.find((x) => x.TABLE_NAME === tn && x.DBNAME === dn)
    if (c) Object.assign(c, patch)
    // The query alias has ONE owner — the source column in TABLES. Editing it
    // on a folder column writes through (and to the column's other folders),
    // so a column placed twice can never end up with two aliases.
    if (patch.UNIQUE_NAME !== undefined) {
      const src = s.TABLES.find((t) => t.TABLE_NAME === tn)?.COLUMNS
        .find((x) => x.DBNAME === dn)
      if (src) src.UNIQUE_NAME = patch.UNIQUE_NAME
      for (const grp of s.COLUMN_GROUPS || []) {
        for (const gc of grp.COLUMNS) {
          if (gc.TABLE_NAME === tn && gc.DBNAME === dn) gc.UNIQUE_NAME = patch.UNIQUE_NAME
        }
      }
    }
  })

  // Merge an imported table (+ its already-resolved columns) into the model.
  // Column resolution happens in ImportTableModal, which is connection-aware
  // (introspects the bound BI connection when one is set, else reads the
  // project's Database_Design).
  const importTable = useCallback((tableName: string, cols: SchemaTableColumn[]) => {
    update((s) => {
      const upper = tableName.toUpperCase()
      const existing = s.TABLES.find((t) => t.TABLE_NAME === upper)
      if (existing) {
        // merge any new columns into the existing table
        const have = new Set(existing.COLUMNS.map((c) => c.DBNAME))
        for (const c of cols) if (!have.has(c.DBNAME)) existing.COLUMNS.push(c)
      } else {
        s.TABLES.push({
          TABLE_NAME: upper,
          TABLE_DISPLAY_NAME: prettify(upper),
          ALIAS: upper.split('_').map((p) => p[0] || '').join('').slice(0, 4),
          COLUMNS: cols,
        })
      }
    })
    // Imported tables stay collapsed — the list can get long; the user expands
    // the one they want (or uses Expand all). Merges into an already-expanded
    // table keep its current state.
  }, [update])

  // Add ONE physical column to a table already in the dataset — used when a
  // join key is picked from the source rather than from the model's column
  // list (see JoinsEditor's columnSource). The SQL builder and the backend
  // review both require a join key to be listed on its table, so selecting it
  // has to bring it in.
  const addSourceColumn = useCallback((tableName: string, col: SchemaTableColumn) => {
    update((s) => {
      const upper = (tableName || '').toUpperCase()
      const t = s.TABLES.find((x) => x.TABLE_NAME === upper)
      if (!t || !col.DBNAME) return
      if (t.COLUMNS.some((c) => c.DBNAME === col.DBNAME)) return
      t.COLUMNS.push(col)
    })
    toast.success(`Added ${tableName}.${col.DBNAME} to the dataset`)
  }, [update])

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height: '100%' }}>
        <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
        <span style={{ marginLeft: 8 }}>Loading schema…</span>
      </div>
    )
  }

  const placedCount = groups.reduce((n, g) => n + g.COLUMNS.length, 0)

  return (
    <div style={embedded && embedFullscreen ? {
      position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--color-bg)',
      display: 'flex', flexDirection: 'column',
    } : {
      display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0,
    }}>
      {/* Header bar */}
      <div style={headerBarStyle}>
        <div className="flex items-center gap-2" style={{ minWidth: 0, flex: 1 }}>
          {!embedded && (
            <button
              style={backBtnStyle}
              title="Back to workbench"
              onClick={() => {
                const selectedNodeId = (location.state as { selectedNodeId?: string })?.selectedNodeId
                navigate('/workbench', { state: { selectedNodeId } })
              }}
            >
              <ArrowLeft size={16} />
            </button>
          )}
          <span style={mChipStyle} title="Data model — which tables & joins your charts can use">M</span>
          <div style={titleStyle}>
            <span style={{ fontWeight: 700, fontSize: 19, letterSpacing: '-0.2px' }}>{sName}</span>
            <span style={pathStyle}>/ {projectName}</span>
            <span style={badgeStyle}>visual schema</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setAiOpen(true)}
            title="Generate with AI — drafts source tables, folders, joins and security from a description"
            style={{ color: 'var(--color-accent)', fontWeight: 600 }}
          >
            <Sparkles size={14} />
            <span style={{ fontSize: 14 }}>Generate</span>
          </button>
          <div style={dividerStyle} />
          {canEdit && (
            <>
              <UndoRedoButtons history={history} />
              <div style={dividerStyle} />
            </>
          )}
          {canEdit ? (
            <button
              className={`btn btn-sm ${isDirty ? 'btn-primary' : 'btn-secondary'}`}
              onClick={handleSave}
              disabled={saving}
              title={isDirty ? 'You have unsaved changes' : 'No changes to save'}
              style={isDirty ? { boxShadow: '0 0 0 3px var(--color-accent-weak)' } : undefined}
            >
              {saving ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={14} />}
              {saving ? 'Saving…' : isDirty ? 'Save *' : 'Save'}
            </button>
          ) : (
            <ReadOnlyBadge />
          )}
          {embedded && !hostFullscreen && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setEmbedFullscreen((f) => !f)}
              title={embedFullscreen ? 'Restore' : 'Full screen'}
            >
              {embedFullscreen ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={tabBarStyle}>
        {([
          ['model', `Business Model${placedCount ? ` (${placedCount})` : ''}`],
          ['joins', `Joins (${schema.JOINS.length})`],
          ['security', `Security (${(schema.ROW_SECURITY || []).length + (schema.COLUMN_SECURITY || []).length})`],
          ['settings', 'Settings'],
        ] as [ViewTab, string][]).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setView(key)}
            style={{ ...tabStyle, ...(view === key ? tabActiveStyle : null) }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Body */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', background: 'var(--color-bg)' }}>
        {view === 'model' && (
          <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={onDragStart} onDragEnd={onDragEnd}>
            <div style={{ display: 'flex', height: '100%', minHeight: 0 }}>
              {/* LEFT — source tables tree */}
              <div style={leftPanelStyle}>
                <div style={panelHeaderStyle}>
                  <div style={{ minWidth: 0 }}>
                    <div style={panelTitleStyle}>
                      <Table2 size={13} style={{ verticalAlign: -2, marginRight: 5 }} />
                      Source Tables ({schema.TABLES.length})
                      {selectedCols.size > 0 && (
                        <span style={selCountBadgeStyle} title="Selected columns — drag any one into a folder to move them all">
                          {selectedCols.size} selected
                          <button
                            onClick={() => setSelectedCols(new Set())}
                            title="Clear selection"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'inline-flex', padding: 0, marginLeft: 3 }}
                          >
                            <X size={11} />
                          </button>
                        </span>
                      )}
                    </div>
                    <div style={panelHelperStyle}>
                      Physical columns from the connection (read-only). Tick columns to multi-select, then
                      drag any one into a folder → — or drag a whole table to turn it into a folder.
                    </div>
                  </div>
                  <div className="flex items-center gap-1" style={{ flexShrink: 0 }}>
                    {schema.TABLES.length > 0 && (
                      <>
                        <button className="btn btn-ghost btn-sm" onClick={expandAll} title="Expand all tables" style={{ padding: '3px 6px' }}>
                          <ChevronsUpDown size={14} />
                        </button>
                        <button className="btn btn-ghost btn-sm" onClick={collapseAll} title="Collapse all tables" style={{ padding: '3px 6px' }}>
                          <ChevronsDownUp size={14} />
                        </button>
                      </>
                    )}
                    {canEdit && (
                      <>
                        <button className="btn btn-secondary btn-sm" onClick={() => setImportOpen(true)} title="Import a table from the selected connection">
                          <Download size={13} /> Import
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => update((s) => { s.TABLES.push(blankTable()) })}
                          title="Add a blank table"
                        >
                          <Plus size={13} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {/* Connection band — which database the source tables come from.
                    Surfaced here (not just in Settings) so it's obvious when the
                    list is empty because no source is chosen. */}
                <div style={connBandStyle}>
                  <span style={connBandLabelStyle}>Connection</span>
                  <ConnectionPicker
                    project={projectName}
                    value={schema.CONNECTION_ID || ''}
                    disabled={!canEdit}
                    onChange={(cid) => update((s) => { s.CONNECTION_ID = cid })}
                  />
                </div>
                <div style={panelBodyStyle}>
                  {schema.TABLES.length === 0 && (
                    <div style={emptyHintStyle}>
                      No tables yet. {schema.CONNECTION_ID
                        ? <>Click <b>Import</b> to pull tables from the selected connection, or <b>+</b> to add one manually.</>
                        : <>Pick a <b>Connection</b> above (or leave it as the project database), then click <b>Import</b> to pull its tables — or <b>+</b> to add one manually.</>}
                    </div>
                  )}
                  {schema.TABLES.map((tbl, ti) => {
                    const tkey = tbl.TABLE_NAME || `__t${ti}`
                    const expanded = expandedTables.has(tkey)
                    const isMain = !!tbl.TABLE_NAME && (schema.MAIN_TABLE || '') === tbl.TABLE_NAME
                    return (
                      <div key={ti} style={{ ...tableCardStyle, ...(isMain ? tableCardMainStyle : null) }}>
                        <div style={treeTableHeaderStyle}>
                          {canEdit && tbl.TABLE_NAME && (
                            <TableDragHandle
                              tableName={tbl.TABLE_NAME}
                              cols={tbl.COLUMNS.map((c) => ({
                                tableName: tbl.TABLE_NAME, dbname: c.DBNAME,
                                coltype: (c.COLTYPE as string) || 'CHAR',
                                label: c.DISPLAY_NAME || prettify(c.DBNAME),
                              }))}
                            />
                          )}
                          <button className="btn btn-ghost btn-sm" onClick={() => toggleTable(tkey)} style={{ padding: '2px 3px' }} title={expanded ? 'Collapse' : 'Expand'}>
                            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </button>
                          <input
                            type="text" value={tbl.TABLE_NAME} readOnly={!canEdit}
                            onChange={(e) => update((s) => { s.TABLES[ti].TABLE_NAME = e.target.value.toUpperCase() })}
                            placeholder="TABLE_NAME"
                            title="Source table name (editable)"
                            style={tableNameInputStyle}
                          />
                          <input
                            type="text" value={tbl.ALIAS || ''} readOnly={!canEdit}
                            onChange={(e) => update((s) => { s.TABLES[ti].ALIAS = e.target.value.toUpperCase() })}
                            placeholder="alias" title="Table alias used in generated SQL"
                            style={aliasChipInputStyle}
                          />
                          <button
                            style={isMain ? mainTableOnStyle : mainTableOffStyle}
                            disabled={!canEdit && !isMain}
                            onClick={() => canEdit && update((s) => {
                              s.MAIN_TABLE = s.MAIN_TABLE === tbl.TABLE_NAME ? '' : tbl.TABLE_NAME
                            })}
                            title={isMain
                              ? 'Main (fact) table — every query starts FROM it and joins outward. Click to unset.'
                              : 'Mark as the main (fact) table — every query starts FROM it and joins outward'}
                          >
                            <Star size={11} fill={isMain ? 'currentColor' : 'none'} style={{ verticalAlign: -1, marginRight: 4 }} />
                            {isMain ? 'Main table' : 'Set as main'}
                          </button>
                          {canEdit && (
                            <button
                              className="btn btn-ghost btn-sm"
                              onClick={() => update((s) => { s.TABLES.splice(ti, 1) })}
                              title="Remove table"
                            >
                              <Trash2 size={12} />
                            </button>
                          )}
                        </div>
                        {expanded && (
                          <div style={tableCardBodyStyle}>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                              <span style={condLabelStyle}>Condition</span>
                              <input
                                type="text" value={tbl.CONDITION || ''} readOnly={!canEdit}
                                onChange={(e) => update((s) => { s.TABLES[ti].CONDITION = e.target.value })}
                                placeholder="e.g. STATUS = 'POSTED' — injected automatically wherever this table is used"
                                title="SQL condition AND-ed into the WHERE automatically whenever this table participates in a query (in addition to row/column security)"
                                style={{ ...inputStyle, flex: 1, fontFamily: MONO_FONT, fontSize: 14 }}
                              />
                              {tbl.CONDITION ? <span style={autoInjectBadgeStyle}>auto-injected</span> : null}
                              {canEdit && (
                                <VariableInsert tokens={varTokens} onInsert={(t) => update((s) => {
                                  s.TABLES[ti].CONDITION = `${s.TABLES[ti].CONDITION || ''} ${t}`.trimStart()
                                })} />
                              )}
                            </div>
                            {tbl.COLUMNS.map((col, ci) => {
                              const skey = colSelKey(tbl.TABLE_NAME, col.DBNAME)
                              return (
                                <SourceColumnRow
                                  key={ci}
                                  table={tbl} col={col}
                                  canEdit={canEdit}
                                  selected={selectedCols.has(skey)}
                                  onToggleSelect={(additive) => toggleSelectCol(skey, additive)}
                                  selectedDragCols={selectedDragCols}
                                  placedFolders={placedIndex.get(skey) || []}
                                />
                              )
                            })}
                            {tbl.COLUMNS.length === 0 && <div style={emptyHintStyle}>No columns. Use <b>Import</b> to pull the table's physical columns.</div>}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* RIGHT — business folders */}
              <div style={rightPanelStyle}>
                <div style={{ ...panelHeaderStyle, background: 'transparent' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={panelTitleStyle}>Business Folders ({groups.length})</div>
                    <div style={panelHelperStyle}>
                      The logical model your visuals see. Drop source columns here, rename them
                      in plain language, and set how numbers aggregate.
                    </div>
                  </div>
                  {canEdit && (
                    <button className="btn btn-secondary btn-sm" onClick={addGroup} title="Add a folder" style={{ flexShrink: 0 }}>
                      <FolderPlus size={13} /> Add Folder
                    </button>
                  )}
                </div>
                <NewFolderZone>
                  {groups.length === 0 && <div style={emptyHintStyle}>No folders. Click "Add Folder", or drag a table / columns here to make one.</div>}
                  <SortableContext items={groups.map((g) => `fsort|${g.GROUP_ID}`)} strategy={verticalListSortingStrategy}>
                    {groups.map((g) => (
                      <FolderCard
                        key={g.GROUP_ID}
                        group={g}
                        canEdit={canEdit}
                        editingCols={editingGroupCol}
                        onToggleEditCol={toggleEditGroupCol}
                        onPatchGroup={patchGroup}
                        onRemoveGroup={removeGroup}
                        onRemoveCol={removeGroupCol}
                        onPatchCol={patchGroupCol}
                        placedIndex={placedIndex}
                        aliasIndex={aliasIndex}
                      />
                    ))}
                  </SortableContext>
                  {schema.TABLES.length > 0 && (
                    <div style={dragHintStyle}>
                      Tip: tick columns on the left to multi-select, then drag any one into a folder.
                      Drag a whole table to make it a folder. Drop on empty space to start a new folder.
                    </div>
                  )}
                </NewFolderZone>
              </div>
            </div>

            {/* dropAnimation={null}: without it dnd-kit animates the chip back to
                the source column before the drop applies — reading as a "reverse
                movement". Killing the animation makes the column just land. */}
            <DragOverlay dropAnimation={null}>
              {activeDrag ? <DragChip drag={activeDrag} /> : null}
            </DragOverlay>
          </DndContext>
        )}

        {view === 'joins' && (
          <div style={scrollOuterStyle}>
            <JoinsEditor
              tables={schema.TABLES}
              mainTable={schema.MAIN_TABLE}
              joins={schema.JOINS}
              canEdit={canEdit}
              onJoinsChange={(next) => update((s) => { s.JOINS = next })}
              layout={schema.JOIN_LAYOUT}
              onLayoutChange={(next) => update((s) => { s.JOIN_LAYOUT = next })}
              onAddTable={() => setImportOpen(true)}
              columnSource={canEdit ? {
                load: (t) => fetchTableColumns(projectName, schema.CONNECTION_ID, t),
                adopt: addSourceColumn,
              } : undefined}
            />
          </div>
        )}

        {view === 'settings' && (
          <div style={scrollOuterStyle}>
            <div style={{ maxWidth: 640 }}>
            <Section title="Schema Settings">
              <div style={settingsCardStyle}>
                <Field label="Schema file name" hint="(read only)">
                  <input type="text" value={schema.SCHEMA_NAME} readOnly
                    style={{ ...inputStyle, width: '100%', fontFamily: MONO_FONT, fontSize: 14.5, background: 'var(--color-surface-2)', color: 'var(--color-text-faint)' }} />
                </Field>
                <Field label="Description" hint="(fact grain, what the schema covers — helps AI generation)">
                  <textarea value={schema.SCHEMA_DESCR} readOnly={!canEdit} rows={3}
                    onChange={(e) => update((s) => { s.SCHEMA_DESCR = e.target.value })}
                    style={{ ...inputStyle, width: '100%', fontFamily: 'inherit', resize: 'vertical' }} />
                </Field>
                <Field
                  label="Connection"
                  hint="(which database this schema reads from — visuals built on it follow automatically)"
                >
                  <ConnectionPicker
                    project={projectName}
                    value={schema.CONNECTION_ID || ''}
                    disabled={!canEdit}
                    onChange={(cid) => update((s) => { s.CONNECTION_ID = cid })}
                  />
                </Field>
                <Field label="Main table" hint="(query root — same as the ★ in Business Model)">
                  <select value={schema.MAIN_TABLE || ''} disabled={!canEdit}
                    onChange={(e) => update((s) => { s.MAIN_TABLE = e.target.value })}
                    style={{ ...selectStyle, maxWidth: 260, fontFamily: MONO_FONT, fontSize: 14.5 }}>
                    <option value="">— none —</option>
                    {schema.TABLES.map((t) => t.TABLE_NAME && <option key={t.TABLE_NAME} value={t.TABLE_NAME}>{t.TABLE_NAME}</option>)}
                  </select>
                </Field>
              </div>
            </Section>
            </div>
          </div>
        )}

        {view === 'security' && (
          <div style={scrollOuterStyle}>
            <div style={{ maxWidth: 980 }}>
            {/* Row security */}
            <Section
              title={`Row Security (${(schema.ROW_SECURITY || []).length})`}
              hint="— filter which rows a role may read"
              right={canEdit && (
                <button className="btn btn-secondary btn-sm"
                  onClick={() => update((s) => { (s.ROW_SECURITY || (s.ROW_SECURITY = [])).push(blankRowSec(s.ROW_SECURITY!.length + 1)) })}>
                  <Plus size={13} /> Add RLS
                </button>
              )}
            >
              {(schema.ROW_SECURITY || []).length === 0 && <div style={emptyHintStyle}>No row-level security policies.</div>}
              <datalist id="schema-rls-tables">
                <option value="ALL">every table in the query</option>
                {schema.TABLES.map((t) => t.TABLE_NAME && <option key={t.TABLE_NAME} value={t.TABLE_NAME} />)}
              </datalist>
              {(schema.ROW_SECURITY || []).map((p, idx) => (
                <div key={idx} style={cardStyle}>
                  <div style={cardHeaderStyle}>
                    <input type="text" value={p.POLICY_ID} readOnly={!canEdit}
                      onChange={(e) => update((s) => { s.ROW_SECURITY![idx].POLICY_ID = e.target.value })}
                      placeholder="RLS_001"
                      style={{ ...idChipInputStyle, width: 96 }} />
                    <input type="text" list="schema-rls-tables" value={p.TABLE_NAME} readOnly={!canEdit}
                      onChange={(e) => update((s) => { s.ROW_SECURITY![idx].TABLE_NAME = e.target.value.toUpperCase() })}
                      placeholder="table (or ALL)" style={{ ...inputStyle, flex: 1 }} />
                    <input type="text" value={(p.APPLIES_TO_ROLES || []).join(', ')} readOnly={!canEdit}
                      onChange={(e) => update((s) => {
                        s.ROW_SECURITY![idx].APPLIES_TO_ROLES = e.target.value.split(',').map((r) => r.trim()).filter(Boolean)
                      })}
                      placeholder="roles (blank = everyone)" style={{ ...inputStyle, flex: 1 }} />
                    {canEdit && (
                      <RoleAdder roles={funcRoles} current={p.APPLIES_TO_ROLES || []}
                        onAdd={(r) => update((s) => {
                          s.ROW_SECURITY![idx].APPLIES_TO_ROLES = [...(s.ROW_SECURITY![idx].APPLIES_TO_ROLES || []), r]
                        })} />
                    )}
                    {canEdit && (
                      <button className="btn btn-ghost btn-sm" onClick={() => update((s) => { s.ROW_SECURITY!.splice(idx, 1) })} title="Remove">
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                  <div style={{ marginTop: 8, display: 'flex', gap: 4, alignItems: 'flex-start' }}>
                    <textarea value={p.CONDITION} readOnly={!canEdit}
                      onChange={(e) => update((s) => { s.ROW_SECURITY![idx].CONDITION = e.target.value })}
                      placeholder="SQL predicate — e.g. SITE_CODE = :current_site or CUST_ID = :current_user_id" rows={2}
                      style={{ ...onStripInputStyle, flex: 1, resize: 'vertical' }} />
                    {canEdit && (
                      <VariableInsert tokens={varTokens} onInsert={(t) => update((s) => {
                        s.ROW_SECURITY![idx].CONDITION = `${s.ROW_SECURITY![idx].CONDITION || ''} ${t}`.trimStart()
                      })} />
                    )}
                  </div>
                  <div style={plainTermsStyle}>
                    <span style={{ color: 'var(--color-info)', fontWeight: 600 }}>In plain terms:</span>{' '}
                    {rlsPlainTerms(p)}
                  </div>
                </div>
              ))}
            </Section>

            {/* Column security */}
            <Section
              title={`Column Security (${(schema.COLUMN_SECURITY || []).length})`}
              hint="— hide or mask a column for a role"
              right={canEdit && (
                <button className="btn btn-secondary btn-sm"
                  onClick={() => update((s) => { (s.COLUMN_SECURITY || (s.COLUMN_SECURITY = [])).push(blankColSec(s.COLUMN_SECURITY!.length + 1)) })}>
                  <Plus size={13} /> Add CLS
                </button>
              )}
            >
              {(schema.COLUMN_SECURITY || []).length === 0 && <div style={emptyHintStyle}>No column-level security policies.</div>}
              <datalist id="schema-cls-tables">
                {schema.TABLES.map((t) => t.TABLE_NAME && <option key={t.TABLE_NAME} value={t.TABLE_NAME} />)}
              </datalist>
              {(schema.COLUMN_SECURITY || []).map((p, idx) => (
                <div key={idx} style={cardStyle}>
                  <datalist id={`schema-cls-cols-${idx}`}>
                    {(p.TABLE_NAME
                      ? tableColumns(schema.TABLES, p.TABLE_NAME)
                      : schema.TABLES.flatMap((t) => (t.COLUMNS || []).map((c) => c.DBNAME).filter(Boolean))
                    ).map((c) => <option key={c} value={c} />)}
                  </datalist>
                  <div style={cardHeaderStyle}>
                    <input type="text" value={p.POLICY_ID} readOnly={!canEdit}
                      onChange={(e) => update((s) => { s.COLUMN_SECURITY![idx].POLICY_ID = e.target.value })}
                      placeholder="CLS_001"
                      style={{ ...idChipInputStyle, width: 96 }} />
                    <input type="text" list="schema-cls-tables" value={p.TABLE_NAME} readOnly={!canEdit}
                      onChange={(e) => update((s) => { s.COLUMN_SECURITY![idx].TABLE_NAME = e.target.value.toUpperCase() })}
                      placeholder="TABLE_NAME" style={{ ...inputStyle, flex: 1 }} />
                    <input type="text" list={`schema-cls-cols-${idx}`} value={p.COLUMN_NAME} readOnly={!canEdit}
                      onChange={(e) => update((s) => { s.COLUMN_SECURITY![idx].COLUMN_NAME = e.target.value.toUpperCase() })}
                      placeholder="COLUMN_NAME" style={{ ...inputStyle, flex: 1 }} />
                    <select value={p.ACTION} disabled={!canEdit}
                      onChange={(e) => update((s) => { s.COLUMN_SECURITY![idx].ACTION = e.target.value })}
                      style={{ ...selectStyle, width: 90 }}>
                      {COL_SEC_ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                    <input type="text" value={(p.APPLIES_TO_ROLES || []).join(', ')} readOnly={!canEdit}
                      onChange={(e) => update((s) => {
                        s.COLUMN_SECURITY![idx].APPLIES_TO_ROLES = e.target.value.split(',').map((r) => r.trim()).filter(Boolean)
                      })}
                      placeholder="roles (blank = everyone)" style={{ ...inputStyle, flex: 1 }} />
                    {canEdit && (
                      <RoleAdder roles={funcRoles} current={p.APPLIES_TO_ROLES || []}
                        onAdd={(r) => update((s) => {
                          s.COLUMN_SECURITY![idx].APPLIES_TO_ROLES = [...(s.COLUMN_SECURITY![idx].APPLIES_TO_ROLES || []), r]
                        })} />
                    )}
                    {canEdit && (
                      <button className="btn btn-ghost btn-sm" onClick={() => update((s) => { s.COLUMN_SECURITY!.splice(idx, 1) })} title="Remove">
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </Section>
            </div>
          </div>
        )}
      </div>

      {importOpen && (
        <ImportTableModal
          project={projectName}
          connectionId={schema.CONNECTION_ID || ''}
          existing={new Set(schema.TABLES.map((t) => t.TABLE_NAME))}
          onClose={() => setImportOpen(false)}
          onImport={importTable}
        />
      )}

      {aiOpen && (
        <div style={modalOverlayStyle} onClick={() => setAiOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} style={modalStyle}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Generate Schema via AI</div>
            <div style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 8 }}>
              Describe the dataset (subject area, fact + lookup tables, joins,
              security needs). The generator will populate the source tables, joins,
              Facts / Dimensions folders and security policies.
            </div>
            <textarea
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              autoFocus rows={6}
              style={{ width: '100%', fontFamily: 'inherit', fontSize: 15.5, padding: '9px 11px', border: '1px solid var(--color-border)', borderRadius: 8, background: 'var(--lp-input-bg, var(--color-bg))', color: 'var(--color-text)', resize: 'vertical' }}
              placeholder="e.g. Sales analytics dataset: DWH_SALES_SUM (fact) joined to DWH_ITEM_MASTER, DWH_CUSTOMER, DWH_SITE master tables; row security so each user only sees their site"
            />
            <div className="flex items-center gap-2" style={{ marginTop: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setAiOpen(false)} disabled={generating}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={handleGenerate} disabled={!aiPrompt.trim() || generating}>
                {generating ? (<><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Generating…</>) : (<><Sparkles size={14} /> Generate</>)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Whole-table drag handle (left) — dragging a table makes it a folder ──
function TableDragHandle({ tableName, cols }: { tableName: string; cols: DragCol[] }) {
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: `tbl|${tableName}`,
    data: { kind: 'table', tableName, cols } as TableDrag,
  })
  return (
    <span
      ref={setNodeRef} {...listeners} {...attributes}
      style={{ ...gripStyle, cursor: 'grab' }}
      title="Drag the whole table into the folders area to turn it into a folder"
    >
      <GripVertical size={13} />
    </span>
  )
}

// ── Right-panel canvas — a droppable that turns a bare drop into a new folder ──
function NewFolderZone({ children }: { children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: 'newfolder', data: { kind: 'newfolder' } as NewFolderDrop })
  return (
    <div ref={setNodeRef} style={{ ...panelBodyStyle, ...(isOver ? newFolderOverStyle : null) }}>
      {children}
    </div>
  )
}

// ── Floating drag chip shown under the cursor while dragging ──────────
function DragChip({ drag }: { drag: ActiveDrag }) {
  if (drag.kind === 'table') {
    return (
      <div style={{ ...dragChipStyle, ...overlayChipStyle }}>
        <Table2 size={13} style={{ opacity: 0.7 }} />
        <span style={{ fontWeight: 600 }}>{drag.tableName}</span>
        <span style={{ fontSize: 13, color: 'var(--color-text-faint)' }}>→ folder ({drag.cols.length})</span>
      </div>
    )
  }
  if (drag.kind === 'foldersort') {
    return (
      <div style={{ ...dragChipStyle, ...overlayChipStyle }}>
        <span style={{ fontSize: 15 }}>{drag.icon}</span>
        <span style={{ fontWeight: 600 }}>{drag.name || 'Folder'}</span>
        <span style={{ fontSize: 13, color: 'var(--color-text-faint)' }}>({drag.count})</span>
      </div>
    )
  }
  if (drag.kind === 'source' && drag.cols.length > 1) {
    return (
      <div style={{ ...dragChipStyle, ...overlayChipStyle }}>
        <GripVertical size={12} style={{ opacity: 0.6 }} />
        <span style={{ fontWeight: 600 }}>{drag.cols.length} columns</span>
      </div>
    )
  }
  const one = drag.kind === 'source' ? drag.cols[0] : drag
  return (
    <div style={{ ...dragChipStyle, ...overlayChipStyle }}>
      <GripVertical size={12} style={{ opacity: 0.6 }} />
      <TypeBadge type={one?.coltype || 'CHAR'} />
      <span style={{ fontWeight: 600 }}>{one?.label || one?.dbname}</span>
    </div>
  )
}

// ── Source column row (left tree) — READ-ONLY physical column ──────
// The left tree mirrors the physical source, so columns can't be edited or
// deleted here. A checkbox multi-selects; the grip drags (single, or the whole
// selection). An "in N folders" badge flags a column already placed elsewhere.
function SourceColumnRow({
  table, col, canEdit, selected, onToggleSelect, selectedDragCols, placedFolders,
}: {
  table: SchemaTable; col: SchemaTableColumn
  canEdit: boolean
  selected: boolean
  onToggleSelect: (additive: boolean) => void
  selectedDragCols: DragCol[]
  placedFolders: string[]
}) {
  const tableName = table.TABLE_NAME
  const coltype = (col.COLTYPE as string) || 'CHAR'
  const label = col.DISPLAY_NAME || prettify(col.DBNAME)
  const canDrag = canEdit && !!col.DBNAME
  const self: DragCol = { tableName, dbname: col.DBNAME, coltype, label }
  // Dragging a selected column carries the whole multi-selection; an unselected
  // column drags just itself.
  const cols: DragCol[] = selected && selectedDragCols.length > 1 ? selectedDragCols : [self]
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `src|${tableName}|${col.DBNAME}`,
    data: { kind: 'source', cols } as SourceDrag,
    disabled: !canDrag,
  })
  const placedCount = placedFolders.length
  const meta = sourceColMeta(col)
  return (
    <div ref={setNodeRef} style={{ ...sourceColStyle, opacity: isDragging ? 0.4 : 1, ...(selected ? sourceColSelectedStyle : null) }}>
      <div style={sourceColMainStyle}>
        <input
          type="checkbox" checked={selected} disabled={!canDrag}
          onChange={() => onToggleSelect(true)}
          title="Select column (tick several, then drag any one to move them together)"
          style={{ cursor: canDrag ? 'pointer' : 'not-allowed', flexShrink: 0 }}
        />
        <span
          {...(canDrag ? listeners : {})} {...attributes}
          style={{ ...gripStyle, cursor: canDrag ? 'grab' : 'not-allowed' }}
          title={col.DBNAME ? (canEdit ? 'Drag into a folder' : 'Read-only') : 'Unnamed column'}
        >
          <GripVertical size={13} />
        </span>
        <TypeBadge type={coltype} />
        <span style={{ flex: 1, minWidth: 0, fontSize: 14.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <span style={{ fontFamily: MONO_FONT }}>{col.DBNAME || '(unnamed)'}</span>
          {col.UNIQUE_NAME ? (
            <span
              style={{ fontFamily: MONO_FONT, fontSize: 13, color: 'var(--color-text-faint)' }}
              title={`Query alias — visuals SELECT this column AS "${col.UNIQUE_NAME}". Edit it on the placed column.`}
            >
              {' → '}{String(col.UNIQUE_NAME)}
            </span>
          ) : null}
        </span>
        {placedCount > 0 && (
          <span style={placedBadgeStyle} title={`Already added to: ${placedFolders.join(', ')}`}>
            <CopyIcon size={10} style={{ verticalAlign: -1, marginRight: 3 }} />
            in {placedCount}
          </span>
        )}
        {col.DEFAULTFUNCTION ? <span style={fnBadgeStyle}>{col.DEFAULTFUNCTION}</span> : null}
      </div>
      {meta.length > 0 && (
        <div style={sourceColMetaStyle}>
          {meta.map((m, i) => (
            <span key={i} style={metaChipStyle} title={`${m.label}: ${m.value}`}>
              <span style={{ color: 'var(--color-text-faint)' }}>{m.label}</span>
              <span style={{ fontWeight: 600, color: 'var(--color-text-secondary)' }}>{m.value}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Allowed-type icon + dropdown (folder header) ───────────────────
// Same glyph language as TypeBadge (# numbers, D dates, A text, ∗ any); the
// small badge doubles as the trigger for a menu that changes ALLOWED_TYPES.
const FOLDER_TYPE_META: Record<string, { glyph: string; label: string; color: string; bg: string }> = {
  ANY: { glyph: '∗', label: 'Any type', color: 'var(--color-text-secondary)', bg: 'var(--color-surface-2)' },
  NUMBER: { glyph: '#', label: 'Numbers', color: 'var(--color-info)', bg: 'var(--color-info-weak)' },
  STRING: { glyph: 'A', label: 'Text', color: 'var(--color-success)', bg: 'var(--color-success-weak)' },
  DATE: { glyph: 'D', label: 'Dates', color: 'var(--color-warning)', bg: 'var(--color-warning-weak)' },
}

function FolderTypeIcon({ value, canEdit, onChange }: {
  value: SchemaColumnGroup['ALLOWED_TYPES']
  canEdit: boolean
  onChange: (t: SchemaColumnGroup['ALLOWED_TYPES']) => void
}) {
  const [open, setOpen] = useState(false)
  const current = (value || 'ANY') as string
  const meta = FOLDER_TYPE_META[current] || FOLDER_TYPE_META.ANY
  return (
    <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
      <button
        type="button"
        onClick={() => canEdit && setOpen((o) => !o)}
        title={`Allowed column type: ${meta.label} (advisory)${canEdit ? ' — click to change' : ''}`}
        style={{
          ...typeBadgeStyle, color: meta.color, background: meta.bg,
          border: 'none', cursor: canEdit ? 'pointer' : 'default', padding: 0,
        }}
      >
        {meta.glyph}
      </button>
      {open && (
        <>
          <span style={{ position: 'fixed', inset: 0, zIndex: 60 }} onClick={() => setOpen(false)} />
          <span style={typeMenuStyle}>
            {ALLOWED_TYPE_OPTIONS.map((t) => {
              const m = FOLDER_TYPE_META[t]
              return (
                <button
                  key={t} type="button"
                  onClick={() => { onChange(t); setOpen(false) }}
                  style={{ ...typeMenuItemStyle, ...(t === current ? typeMenuItemActiveStyle : null) }}
                >
                  <span style={{ ...typeBadgeStyle, color: m.color, background: m.bg }}>{m.glyph}</span>
                  <span>{m.label}</span>
                </button>
              )
            })}
          </span>
        </>
      )}
    </span>
  )
}

// ── Folder card (right) — sortable (reorder folders) + droppable + sortable placed columns ──
function FolderCard({
  group, canEdit, editingCols, onToggleEditCol, onPatchGroup, onRemoveGroup, onRemoveCol, onPatchCol, placedIndex,
  aliasIndex,
}: {
  group: SchemaColumnGroup; canEdit: boolean
  editingCols: Set<string>; onToggleEditCol: (k: string) => void
  onPatchGroup: (gid: string, patch: Partial<SchemaColumnGroup>) => void
  onRemoveGroup: (gid: string) => void
  onRemoveCol: (gid: string, tn: string, dn: string) => void
  onPatchCol: (gid: string, tn: string, dn: string, patch: Partial<SchemaGroupColumn>) => void
  placedIndex: Map<string, string[]>
  /** alias → the columns claiming it, for clash detection. */
  aliasIndex: Map<string, string[]>
}) {
  const [collapsed, setCollapsed] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  // Whole-card sortable — drag by the header grip to reorder folders.
  const {
    attributes: sortAttrs, listeners: sortListeners, setNodeRef: setSortRef,
    transform, transition, isDragging,
  } = useSortable({
    id: `fsort|${group.GROUP_ID}`,
    data: {
      kind: 'foldersort', groupId: group.GROUP_ID,
      name: group.GROUP_NAME, icon: group.GROUP_ICON || '📁', count: group.COLUMNS.length,
    } as FolderSortDrag,
    disabled: !canEdit,
  })
  // Droppable on the CARD (not the body) so a collapsed folder still accepts
  // column drops.
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: `folder|${group.GROUP_ID}`, data: { kind: 'folder', groupId: group.GROUP_ID } as FolderDrop })
  const setRefs = (el: HTMLElement | null) => { setSortRef(el); setDropRef(el) }
  const itemIds = group.COLUMNS.map((c) => `col|${group.GROUP_ID}|${colKey(c)}`)
  // Sibling column names in this folder — companion pickers in the format editor.
  const siblingNames = group.COLUMNS.map((c) => c.DBNAME).filter(Boolean)
  return (
    <div
      ref={setRefs}
      style={{
        ...folderCardStyle,
        ...(isOver ? folderOverStyle : null),
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
      }}
    >
      <div style={folderHeaderStyle}>
        {canEdit && (
          <span
            {...sortListeners} {...sortAttrs}
            style={{ ...gripStyle, cursor: 'grab' }}
            title="Drag to reorder folders"
          >
            <GripVertical size={13} />
          </span>
        )}
        <button
          className="btn btn-ghost btn-sm" style={{ padding: '2px 3px' }}
          onClick={() => setCollapsed((c) => !c)}
          title={collapsed ? 'Expand columns' : 'Collapse columns'}
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
        </button>
        <span style={{ fontSize: 16 }}>{group.GROUP_ICON || '📁'}</span>
        <input type="text" value={group.GROUP_NAME} readOnly={!canEdit}
          onChange={(e) => onPatchGroup(group.GROUP_ID, { GROUP_NAME: e.target.value })}
          placeholder="Folder name" title="Folder name — rename in plain business language"
          style={folderNameInputStyle} />
        {group.GROUP_DISPLAY_NAME && (
          <span style={folderDisplayChipStyle} title={`Shown as "${group.GROUP_DISPLAY_NAME}" when picking columns in a visual`}>
            {group.GROUP_DISPLAY_NAME}
          </span>
        )}
        <FolderTypeIcon
          value={group.ALLOWED_TYPES}
          canEdit={canEdit}
          onChange={(t) => onPatchGroup(group.GROUP_ID, { ALLOWED_TYPES: t })}
        />
        <span style={folderCountStyle}>{group.COLUMNS.length}</span>
        <button
          className="btn btn-ghost btn-sm" style={{ padding: '2px 4px', ...(settingsOpen ? { color: 'var(--color-accent)' } : null) }}
          onClick={() => setSettingsOpen((o) => !o)}
          title="Folder settings — display name in visuals, description"
        >
          <Pencil size={11} />
        </button>
        {canEdit && (
          <button className="btn btn-ghost btn-sm" onClick={() => onRemoveGroup(group.GROUP_ID)} title="Remove folder">
            <Trash2 size={12} />
          </button>
        )}
      </div>
      {settingsOpen && (
        <div style={folderSettingsStyle}>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label style={placedFieldLabelStyle}>Display name in visuals</label>
            <input
              type="text" value={group.GROUP_DISPLAY_NAME || ''} readOnly={!canEdit}
              onChange={(e) => onPatchGroup(group.GROUP_ID, { GROUP_DISPLAY_NAME: e.target.value })}
              placeholder={group.GROUP_NAME || 'Folder name'}
              title="Folder label shown when picking columns in a visual — leave blank to use the folder name"
              style={{ ...inputStyle, width: '100%', fontSize: 14.5 }}
            />
          </div>
          <div style={{ flex: 1.4, minWidth: 200 }}>
            <label style={placedFieldLabelStyle}>Description</label>
            <input
              type="text" value={group.DESCRIPTION || ''} readOnly={!canEdit}
              onChange={(e) => onPatchGroup(group.GROUP_ID, { DESCRIPTION: e.target.value })}
              placeholder="What this folder holds — helps the AI and pickers"
              style={{ ...inputStyle, width: '100%', fontSize: 14.5 }}
            />
          </div>
        </div>
      )}
      {collapsed ? (
        <div
          style={{ ...folderCollapsedStripStyle, ...(isOver ? { background: 'var(--color-accent-weak)' } : null) }}
          onClick={() => setCollapsed(false)}
          title="Expand columns"
        >
          {group.COLUMNS.length === 0
            ? 'No columns'
            : group.COLUMNS.map((c) => c.DISPLAY_NAME || prettify(c.DBNAME)).slice(0, 6).join(' · ')
              + (group.COLUMNS.length > 6 ? ` · +${group.COLUMNS.length - 6} more` : '')}
        </div>
      ) : (
        <div style={{ ...folderBodyStyle, ...(isOver ? { background: 'var(--color-accent-weak)' } : null) }}>
          {group.COLUMNS.length === 0 && (
            <div style={{ ...emptyHintStyle, textAlign: 'center', padding: '14px 6px' }}>
              Drop columns here from the source tables
            </div>
          )}
          <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
            {group.COLUMNS.map((c) => {
              const dupCount = (placedIndex.get(colSelKey(c.TABLE_NAME, c.DBNAME)) || []).length
              return (
                <PlacedColumn
                  key={colKey(c)}
                  group={group} col={c} canEdit={canEdit}
                  siblingNames={siblingNames}
                  dupCount={dupCount}
                  editing={editingCols.has(`${group.GROUP_ID}|${colKey(c)}`)}
                  onToggleEdit={() => onToggleEditCol(`${group.GROUP_ID}|${colKey(c)}`)}
                  onRemove={() => onRemoveCol(group.GROUP_ID, c.TABLE_NAME, c.DBNAME)}
                  aliasIndex={aliasIndex}
                  onPatch={(patch) => onPatchCol(group.GROUP_ID, c.TABLE_NAME, c.DBNAME, patch)}
                />
              )
            })}
          </SortableContext>
        </div>
      )}
    </div>
  )
}

// A placed (folder) column — the BUSINESS layer, so it carries full presentation
// metadata: business heading, description, aggregation, and a rich display format
// (the same editor the Visual designer uses; the format is inherited by any
// visual built on this schema). A "in N folders" badge flags cross-folder reuse.
function PlacedColumn({
  group, col, canEdit, siblingNames, dupCount, aliasIndex, editing, onToggleEdit, onRemove, onPatch,
}: {
  group: SchemaColumnGroup; col: SchemaGroupColumn; canEdit: boolean
  siblingNames: string[]; dupCount: number
  aliasIndex: Map<string, string[]>
  editing: boolean; onToggleEdit: () => void; onRemove: () => void
  onPatch: (patch: Partial<SchemaGroupColumn>) => void
}) {
  const [formatOpen, setFormatOpen] = useState(false)
  const coltype = (col.COLTYPE as string) || 'CHAR'
  const label = col.DISPLAY_NAME || prettify(col.DBNAME)
  const id = `col|${group.GROUP_ID}|${colKey(col)}`
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    data: { kind: 'placed', groupId: group.GROUP_ID, tableName: col.TABLE_NAME, dbname: col.DBNAME, coltype, label } as PlacedDrag,
    disabled: !canEdit,
  })
  const fits = typeFitsFolder(coltype, group.ALLOWED_TYPES)
  // The query alias: what a visual built on this schema SELECTs the column AS.
  // Defaults to TABLE_COLUMN so two tables sharing a column name never collide.
  const alias = String(col.UNIQUE_NAME || '')
  const suggested = uniqueColumnName(col.TABLE_NAME, col.DBNAME)
  const aliasOwners = aliasIndex.get(alias.trim().toUpperCase()) || []
  const aliasClash = alias.trim() !== '' && aliasOwners.length > 1
  const display = col.display as DisplayFormat | undefined
  const hasFormat = !!(display && display.type && display.type !== 'text')
  const style: React.CSSProperties = {
    ...placedColStyle,
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }
  // Adapter for the shared DisplayFormatEditor (keyed by DBNAME so its live
  // preview + companion pickers resolve).
  const formatColumn: ColumnDef = {
    name: col.DBNAME,
    db_name: col.DBNAME,
    heading: label,
    col_type: coltype,
    display,
  } as ColumnDef
  return (
    <div ref={setNodeRef} style={style}>
      <div style={placedColMainStyle}>
        <span {...(canEdit ? listeners : {})} {...attributes} style={{ ...gripStyle, cursor: canEdit ? 'grab' : 'default' }} title={canEdit ? 'Drag to reorder / move' : 'Read-only'}>
          <GripVertical size={13} />
        </span>
        <TypeBadge type={coltype} />
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <span style={{ fontWeight: 600, fontSize: 15, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
          <span style={{ fontSize: 13, color: 'var(--color-text-faint)', fontFamily: MONO_FONT }}>
            {col.TABLE_NAME}.{col.DBNAME}
            {alias && (
              <span
                style={{ color: aliasClash ? 'var(--color-warning, #d97706)' : 'var(--color-text-faint)' }}
                title={aliasClash
                  ? `Alias "${alias}" is also used by ${aliasOwners.filter((o) => o !== `${col.TABLE_NAME}.${col.DBNAME}`).join(', ')} — two columns cannot share a query alias`
                  : `Query alias — visuals SELECT this column AS "${alias}"`}
              >
                {' → '}{alias}{aliasClash ? ' ⚠' : ''}
              </span>
            )}
          </span>
        </span>
        {dupCount > 1 && (
          <span style={placedBadgeStyle} title={`This column is also placed in other folders (in ${dupCount} folders total)`}>
            <CopyIcon size={10} style={{ verticalAlign: -1, marginRight: 3 }} />
            in {dupCount}
          </span>
        )}
        {hasFormat && (
          <span style={fmtBadgeStyle} title={`Display format: ${display?.type}`}>
            <Paintbrush size={10} style={{ verticalAlign: -1, marginRight: 3 }} />
            {display?.type}
          </span>
        )}
        {!fits && (
          <span title={`Folder expects ${group.ALLOWED_TYPES}`} style={{ color: 'var(--color-warning, #d97706)', display: 'inline-flex' }}>
            <AlertTriangle size={12} />
          </span>
        )}
        {canEdit && (
          <select value={col.DEFAULTFUNCTION || ''} onChange={(e) => onPatch({ DEFAULTFUNCTION: e.target.value })}
            style={{ ...selectStyle, width: 78, padding: '3px 5px', fontSize: 13 }} title="Aggregation">
            {FUNCTION_OPTIONS.map((f) => <option key={f} value={f}>{f || 'none'}</option>)}
          </select>
        )}
        {canEdit && (
          <>
            <button className="btn btn-ghost btn-sm" style={{ padding: '1px 3px' }} onClick={onToggleEdit} title="Column properties"><Pencil size={11} /></button>
            <button className="btn btn-ghost btn-sm" style={{ padding: '1px 3px' }} onClick={onRemove} title="Remove from folder"><X size={12} /></button>
          </>
        )}
      </div>
      {editing && canEdit && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 7, paddingLeft: 22 }}>
          <label style={placedFieldLabelStyle}>
            Query alias{' '}
            <span style={{ fontWeight: 400, color: 'var(--color-text-faint)' }}>
              — unique in this dataset; visuals SELECT the column AS this
            </span>
          </label>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              type="text" value={alias}
              onChange={(e) => onPatch({ UNIQUE_NAME: e.target.value.toUpperCase() })}
              placeholder={suggested}
              style={{
                ...inputStyle, flex: 1, fontFamily: MONO_FONT,
                ...(aliasClash ? { borderColor: 'var(--color-warning, #d97706)' } : null),
              }}
            />
            {alias !== suggested && (
              <button className="btn btn-ghost btn-sm" style={{ whiteSpace: 'nowrap' }}
                onClick={() => onPatch({ UNIQUE_NAME: suggested })}
                title={`Reset to ${suggested}`}>
                Use {suggested}
              </button>
            )}
          </div>
          {aliasClash && (
            <span style={{ fontSize: 13, color: 'var(--color-warning, #d97706)' }}>
              Also used by {aliasOwners.filter((o) => o !== `${col.TABLE_NAME}.${col.DBNAME}`).join(', ')}.
              Two columns sharing an alias break the query.
            </span>
          )}
          <label style={placedFieldLabelStyle}>Business heading</label>
          <input type="text" value={col.DISPLAY_NAME || ''} onChange={(e) => onPatch({ DISPLAY_NAME: e.target.value })}
            placeholder="Business heading" style={inputStyle} />
          <label style={placedFieldLabelStyle}>Description</label>
          <input type="text" value={col.DESCRIPTION || ''} onChange={(e) => onPatch({ DESCRIPTION: e.target.value })}
            placeholder="Description shown to the AI / in pickers" style={inputStyle} />
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={placedFieldLabelStyle}>Type</label>
              <select value={coltype} onChange={(e) => onPatch({ COLTYPE: e.target.value as SchemaGroupColumn['COLTYPE'] })}
                style={{ ...selectStyle, width: '100%' }}>
                {COLTYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={placedFieldLabelStyle}>Aggregation</label>
              <select value={col.DEFAULTFUNCTION || ''} onChange={(e) => onPatch({ DEFAULTFUNCTION: e.target.value })}
                style={{ ...selectStyle, width: '100%' }}>
                {FUNCTION_OPTIONS.map((f) => <option key={f} value={f}>{f || 'none'}</option>)}
              </select>
            </div>
          </div>
          <label style={placedFieldLabelStyle}>Display format</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="text" readOnly value={hasFormat ? (display?.type || '') : 'Plain text'}
              style={{ ...inputStyle, flex: 1, opacity: 0.85 }} />
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setFormatOpen(true)} title="Set how this column is formatted — carried into any visual built on this schema">
              <Paintbrush size={12} /> Edit…
            </button>
          </div>
          <div style={{ fontSize: 13, color: 'var(--color-text-faint)' }}>
            The format flows into visuals: selecting this column in a chart pre-applies it.
          </div>
        </div>
      )}
      <DisplayFormatEditor
        open={formatOpen}
        onClose={() => setFormatOpen(false)}
        column={formatColumn}
        formColumns={siblingNames}
        onSave={(d) => onPatch({ display: d })}
      />
    </div>
  )
}

// ── Import-from-DB modal ───────────────────────────────────────────
// Lists tables from the schema's bound external connection (Oracle / Dremio /
// warehouse / any BI connection) when one is set, else from the project's own
// Database_Design. Without connection-awareness the picker only ever saw the
// local design JSONs, so an Oracle/Dremio-backed schema showed no tables.
function ImportTableModal({
  project, connectionId, existing, onClose, onImport,
}: {
  project: string; connectionId?: string; existing: Set<string>
  onClose: () => void; onImport: (table: string, cols: SchemaTableColumn[]) => void
}) {
  const conn = (connectionId || '').trim()
  const [tables, setTables] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const [filter, setFilter] = useState('')

  useEffect(() => {
    let cancelled = false
    setLoading(true); setError('')
    // Connection-aware in ONE place (tableSource): the bound BI connection
    // when the schema has one, else the project's Database_Design.
    listSourceTables(project, conn)
      .then((list) => { if (!cancelled) setTables(list) })
      .catch((err) => {
        if (cancelled) return
        console.error('Failed to list tables', err)
        setTables([])
        setError(err?.response?.data?.detail || 'Could not list tables for this connection.')
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [project, conn])

  const filtered = useMemo(
    () => tables.filter((t) => t.toLowerCase().includes(filter.toLowerCase())),
    [tables, filter],
  )

  const importOne = async (t: string) => {
    setBusy(t)
    try {
      const cols = await fetchTableColumns(project, conn, t)
      onImport(t, cols)
      toast.success(`Imported ${t} (${cols.length} columns)`)
    } catch (err) {
      console.error('Import table failed', err)
      toast.error(`Failed to import ${t}`)
    } finally { setBusy(null) }
  }

  return (
    <div style={modalOverlayStyle} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...modalStyle, width: 460 }}>
        <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
          <div style={{ fontWeight: 600 }}>{conn ? 'Import table from connection' : 'Import table from database'}</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={15} /></button>
        </div>
        <input type="text" value={filter} onChange={(e) => setFilter(e.target.value)} autoFocus
          placeholder="Filter tables…" style={{ ...inputStyle, width: '100%', marginBottom: 8 }} />
        <div style={{ maxHeight: 340, overflow: 'auto' }}>
          {loading && (
            <div style={{ padding: 16, textAlign: 'center', color: 'var(--color-text-secondary)' }}>
              <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading…
            </div>
          )}
          {!loading && error && <div style={emptyHintStyle}>{error}</div>}
          {!loading && !error && filtered.length === 0 && (
            <div style={emptyHintStyle}>
              {conn ? 'No tables found on this connection.' : "No tables found in the project's Database Design."}
            </div>
          )}
          {!loading && !error && filtered.map((t) => {
            const already = existing.has(t.toUpperCase())
            return (
              <div key={t} style={importRowStyle}>
                <Table2 size={14} style={{ opacity: 0.6 }} />
                <span style={{ flex: 1, fontFamily: MONO_FONT, fontSize: 14.5 }}>{t}</span>
                <button
                  className="btn btn-secondary btn-sm"
                  disabled={busy === t}
                  onClick={() => importOne(t)}
                  title={already ? 'Re-import / merge new columns' : 'Import this table'}
                >
                  {busy === t ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Download size={12} />}
                  {already ? 'Re-import' : 'Import'}
                </button>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ── tiny presentational helpers ────────────────────────────────────
function TypeBadge({ type }: { type: string }) {
  const t = (type || 'CHAR').toUpperCase()
  // Prototype map — # on info for numbers, D on warn for dates, A on success for strings.
  const color = t === 'NUMBER' ? 'var(--color-info)' : t === 'DATE' ? 'var(--color-warning)' : 'var(--color-success)'
  const bg = t === 'NUMBER' ? 'var(--color-info-weak)' : t === 'DATE' ? 'var(--color-warning-weak)' : 'var(--color-success-weak)'
  const label = t === 'NUMBER' ? '#' : t === 'DATE' ? 'D' : 'A'
  return (
    <span style={{ ...typeBadgeStyle, color, background: bg }} title={t}>
      {label}
    </span>
  )
}

function Section({ title, hint, right, children }: {
  title: string; hint?: string; right?: React.ReactNode; children: React.ReactNode
}) {
  return (
    <div style={{ marginBottom: 26 }}>
      <div style={sectionHeaderStyle}>
        <span style={{ minWidth: 0 }}>
          <span style={{ fontWeight: 700, fontSize: 15, letterSpacing: '-0.1px' }}>{title}</span>
          {hint && <span style={{ marginLeft: 8, fontSize: 14, fontWeight: 400, color: 'var(--color-text-secondary)' }}>{hint}</span>}
        </span>
        {right}
      </div>
      {children}
    </div>
  )
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)', marginBottom: 5 }}>
        {label}
        {hint && <span style={{ marginLeft: 6, fontWeight: 400, color: 'var(--color-text-faint)' }}>{hint}</span>}
      </div>
      {children}
    </div>
  )
}

const inputStyle: React.CSSProperties = {
  font: 'inherit', padding: '7px 10px', fontSize: 15.5, border: '1px solid var(--color-border)',
  borderRadius: 7, background: 'var(--lp-input-bg, var(--color-bg))', color: 'var(--color-text)',
}
const selectStyle: React.CSSProperties = { ...inputStyle }
// Join / security policy cards — surface, radius 11, per the prototype list rows.
const cardStyle: React.CSSProperties = {
  border: '1px solid var(--color-border)', borderRadius: 11, padding: '14px 16px', marginBottom: 12,
  background: 'var(--color-bg-secondary)',
}
const cardHeaderStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }
const sectionHeaderStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
  marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid var(--color-border)',
}
const emptyHintStyle: React.CSSProperties = {
  fontSize: 14, color: 'var(--color-text-faint)', fontStyle: 'italic', padding: '4px 6px',
}
const helperTextStyle: React.CSSProperties = {
  fontSize: 14, color: 'var(--color-text-secondary)',
}
// Mono id chip (J001 / RLS_001 / CLS_001) — editable, but reads as the prototype chip.
const idChipInputStyle: React.CSSProperties = {
  width: 64, padding: '4px 8px', fontFamily: MONO_FONT, fontSize: 13, fontWeight: 700,
  color: 'var(--color-text-secondary)', background: 'var(--color-surface-2)',
  border: '1px solid var(--color-border)', borderRadius: 5, flexShrink: 0,
}
// ON-condition strip — mono on the input surface, radius 7 (prototype ON preview).
const onStripInputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 11px', fontFamily: MONO_FONT, fontSize: 14.5,
  color: 'var(--color-text)', background: 'var(--lp-input-bg, var(--color-bg))',
  border: '1px solid var(--color-border)', borderRadius: 7,
}

// header chrome — surface bar with bottom border (§7.2)
const headerBarStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
  padding: '14px 20px', flexShrink: 0,
  background: 'var(--color-bg-secondary)', borderBottom: '1px solid var(--color-border)',
}
// circular back button (36px, border, surface) — prototype header chrome
const backBtnStyle: React.CSSProperties = {
  width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  border: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)',
  color: 'var(--color-text-secondary)', cursor: 'pointer',
}
const mChipStyle: React.CSSProperties = {
  width: 24, height: 24, lineHeight: '24px', textAlign: 'center', flexShrink: 0,
  background: M_CHIP_COLOR, color: '#fff', fontWeight: 700, fontSize: 14, borderRadius: 6,
  userSelect: 'none',
}
const titleStyle: React.CSSProperties = {
  minWidth: 0, fontSize: 15, color: 'var(--color-text)', fontWeight: 500,
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
}
const pathStyle: React.CSSProperties = {
  marginLeft: 8, fontSize: 14, fontFamily: MONO_FONT, color: 'var(--color-text-faint)',
}
const badgeStyle: React.CSSProperties = {
  marginLeft: 8, fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary)',
  background: 'var(--color-surface-2)', border: '1px solid var(--color-border)',
  borderRadius: 6, padding: '2px 8px', verticalAlign: 2,
}
const dividerStyle: React.CSSProperties = { width: 1, height: 20, background: 'var(--color-border)' }
const modalOverlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50,
}
const modalStyle: React.CSSProperties = {
  background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', borderRadius: 16,
  padding: 22, width: 580, maxWidth: '90vw', boxShadow: '0 8px 28px rgba(0,0,0,0.35)',
}

// tabs — underline style on the surface chrome (§0.3)
const tabBarStyle: React.CSSProperties = {
  display: 'flex', gap: 22, padding: '0 20px', flexShrink: 0,
  background: 'var(--color-bg-secondary)', borderBottom: '1px solid var(--color-border)',
}
const tabStyle: React.CSSProperties = {
  padding: '10px 2px', fontSize: 15, fontWeight: 500, border: 'none', borderBottom: '2px solid transparent',
  background: 'transparent', color: 'var(--color-text-secondary)', cursor: 'pointer',
}
const tabActiveStyle: React.CSSProperties = {
  color: 'var(--color-text)', fontWeight: 600, borderBottomColor: 'var(--color-accent)',
}

// two-pane workspace — left Source Tables (surface), right Business Folders canvas (page bg).
// Spec grid is 1.05fr / 1fr (§7.3); each pane scrolls under a fixed pane header.
const leftPanelStyle: React.CSSProperties = {
  width: '51%', display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0,
  borderRight: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)',
}
const rightPanelStyle: React.CSSProperties = {
  flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0,
  background: 'var(--color-bg)',
}
const panelHeaderStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
  padding: '18px 20px 12px', borderBottom: '1px solid var(--color-border)',
  background: 'var(--color-bg-secondary)', flexShrink: 0,
}
const panelTitleStyle: React.CSSProperties = { fontWeight: 700, fontSize: 14 }
const panelHelperStyle: React.CSSProperties = {
  marginTop: 4, fontSize: 14, color: 'var(--color-text-faint)',
}
const panelBodyStyle: React.CSSProperties = { flex: 1, minHeight: 0, overflowY: 'auto', padding: '14px 20px 28px' }
const connBandStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 20px',
  borderBottom: '1px solid var(--color-border)', background: 'var(--color-surface-2, var(--color-bg-secondary))',
  flexShrink: 0,
}
const connBandLabelStyle: React.CSSProperties = {
  fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary)', whiteSpace: 'nowrap',
}
const scrollOuterStyle: React.CSSProperties = {
  height: '100%', overflowY: 'auto', padding: '20px 24px 60px',
}

// source table cards — surface cards, main table gets the warn border (§7.3.1)
const tableCardStyle: React.CSSProperties = {
  border: '1px solid var(--color-border)', borderRadius: 11, padding: '9px 11px', marginBottom: 10,
  background: 'var(--color-bg-secondary)',
}
const tableCardMainStyle: React.CSSProperties = { borderColor: 'var(--color-warning)' }
const tableCardBodyStyle: React.CSSProperties = {
  marginTop: 9, paddingTop: 9, paddingLeft: 6, borderTop: '1px solid var(--color-border)',
}
const treeTableHeaderStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6 }
// table name — inline-editable, reads as the prototype's mono 13px/600 label
const tableNameInputStyle: React.CSSProperties = {
  flex: 1, minWidth: 0, fontFamily: MONO_FONT, fontSize: 14, fontWeight: 600,
  color: 'var(--color-text)', background: 'transparent', border: '1px solid transparent',
  borderRadius: 5, padding: '3px 4px', outline: 'none',
}
// alias — mono 11px surface-2 chip (editable)
const aliasChipInputStyle: React.CSSProperties = {
  width: 56, fontFamily: MONO_FONT, fontSize: 13, fontWeight: 600, textAlign: 'center',
  color: 'var(--color-text-secondary)', background: 'var(--color-surface-2)',
  border: '1px solid var(--color-border)', borderRadius: 5, padding: '3px 4px', flexShrink: 0,
}
const condLabelStyle: React.CSSProperties = {
  fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary)', flexShrink: 0,
}
const autoInjectBadgeStyle: React.CSSProperties = {
  fontSize: 13, fontWeight: 600, color: 'var(--color-warning)', flexShrink: 0,
  background: 'var(--color-warning-weak)', borderRadius: 6, padding: '2px 8px',
}
// main-table star badge — warm gold border/text on transparent (prototype)
const mainTableOnStyle: React.CSSProperties = {
  fontSize: 13, fontWeight: 600, color: 'var(--color-warning)', cursor: 'pointer', flexShrink: 0,
  background: 'transparent',
  border: '1px solid var(--color-warning)', borderRadius: 7, padding: '3px 9px',
}
const mainTableOffStyle: React.CSSProperties = {
  fontSize: 13, fontWeight: 500, color: 'var(--color-text-faint)', cursor: 'pointer', flexShrink: 0,
  background: 'transparent', border: '1px solid var(--color-border)', borderRadius: 7, padding: '3px 9px',
}
// column rows separated by a 1px border (prototype source-table anatomy)
const sourceColStyle: React.CSSProperties = {
  borderBottom: '1px solid var(--color-border)', padding: '5px 4px 5px 5px',
  background: 'transparent',
}
const sourceColMainStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 7 }
// read-only property chips under a source column (type · width · default · …)
const sourceColMetaStyle: React.CSSProperties = {
  display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 4, paddingLeft: 44,
}
const metaChipStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13,
  fontFamily: MONO_FONT, whiteSpace: 'nowrap', background: 'var(--color-surface-2)',
  border: '1px solid var(--color-border)', borderRadius: 4, padding: '0 5px', lineHeight: '17px',
}

// folders — dashed drop targets that step off the page canvas (§7.3.2)
const folderCardStyle: React.CSSProperties = {
  border: '1.5px dashed var(--color-border-strong)', borderRadius: 10, marginBottom: 14,
  background: 'var(--color-bg-secondary)', overflow: 'hidden',
}
const folderOverStyle: React.CSSProperties = {
  borderColor: 'var(--color-accent)', boxShadow: '0 0 0 3px var(--color-accent-weak)',
}
const folderHeaderStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 7, padding: '10px 12px', borderBottom: '1px solid var(--color-border)',
}
const folderNameInputStyle: React.CSSProperties = {
  font: 'inherit', flex: 1, minWidth: 0, fontWeight: 700, fontSize: 14.5, color: 'var(--color-text)',
  background: 'transparent', border: 'none', borderBottom: '1px solid transparent', outline: 'none',
  padding: '2px 2px',
}
// display-name chip next to the folder name — how the folder reads in visuals
const folderDisplayChipStyle: React.CSSProperties = {
  fontSize: 13, fontWeight: 600, color: 'var(--color-accent)', background: 'var(--color-accent-weak)',
  borderRadius: 6, padding: '2px 8px', flexShrink: 0, maxWidth: 160,
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
}
// folder settings sub-row (display name in visuals + description)
const folderSettingsStyle: React.CSSProperties = {
  display: 'flex', gap: 10, flexWrap: 'wrap', padding: '9px 12px 11px',
  borderBottom: '1px solid var(--color-border)', background: 'var(--color-surface-2, var(--color-bg-secondary))',
}
// one-line summary shown instead of the column list when a folder is collapsed
const folderCollapsedStripStyle: React.CSSProperties = {
  padding: '7px 12px', fontSize: 13, color: 'var(--color-text-faint)', cursor: 'pointer',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', transition: 'background 0.12s',
}
// allowed-type dropdown menu (opens from the type icon in the folder header)
const typeMenuStyle: React.CSSProperties = {
  position: 'absolute', top: 22, right: 0, zIndex: 61, minWidth: 136,
  display: 'flex', flexDirection: 'column', gap: 1, padding: 4,
  background: 'var(--color-raised, var(--color-bg-secondary))', border: '1px solid var(--color-border-strong)',
  borderRadius: 8, boxShadow: '0 8px 28px rgba(0,0,0,0.3)',
}
const typeMenuItemStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', fontSize: 14,
  border: 'none', borderRadius: 6, background: 'transparent', color: 'var(--color-text)',
  cursor: 'pointer', textAlign: 'left', whiteSpace: 'nowrap',
}
const typeMenuItemActiveStyle: React.CSSProperties = {
  background: 'var(--color-accent-weak)', fontWeight: 600,
}
const folderBodyStyle: React.CSSProperties = { padding: '10px 12px', minHeight: 52, transition: 'background 0.12s' }
const folderCountStyle: React.CSSProperties = {
  fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary)', minWidth: 18, textAlign: 'center',
  background: 'var(--color-surface-2)', borderRadius: 10, padding: '1px 8px', flexShrink: 0,
}
// placed columns — surface-2 chip rows (§7.3.2)
const placedColStyle: React.CSSProperties = {
  border: '1px solid var(--color-border)', borderRadius: 7, padding: '6px 8px', marginBottom: 6,
  background: 'var(--color-surface-2)',
}
const placedColMainStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 7 }
const placedFieldLabelStyle: React.CSSProperties = {
  fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary)', marginTop: 2,
}
// "in N folders" badge — subtle info chip flagging a reused column
const placedBadgeStyle: React.CSSProperties = {
  fontSize: 13, fontWeight: 700, color: 'var(--color-info)', background: 'var(--color-info-weak)',
  borderRadius: 4, padding: '1px 6px', flexShrink: 0, display: 'inline-flex', alignItems: 'center',
}
// display-format chip on a placed column
const fmtBadgeStyle: React.CSSProperties = {
  fontSize: 13, fontWeight: 700, color: 'var(--color-accent)', background: 'var(--color-accent-weak)',
  borderRadius: 4, padding: '1px 6px', flexShrink: 0, display: 'inline-flex', alignItems: 'center',
}
// selected-count chip in the Source Tables header
const selCountBadgeStyle: React.CSSProperties = {
  marginLeft: 8, fontSize: 13, fontWeight: 600, color: 'var(--color-accent)', background: 'var(--color-accent-weak)',
  borderRadius: 6, padding: '1px 6px 1px 8px', display: 'inline-flex', alignItems: 'center', gap: 2, verticalAlign: 1,
}
const sourceColSelectedStyle: React.CSSProperties = {
  background: 'var(--color-accent-weak)', borderRadius: 6,
}
// right-panel canvas highlight while a table/columns hover over empty space
const newFolderOverStyle: React.CSSProperties = {
  boxShadow: 'inset 0 0 0 2px var(--color-accent)', borderRadius: 10,
}

const gripStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', color: 'var(--color-text-faint)', touchAction: 'none', flexShrink: 0,
}
// type chip — 18px rounded, # / D / A glyph (prototype column anatomy)
const typeBadgeStyle: React.CSSProperties = {
  fontSize: 13, fontWeight: 700, width: 18, height: 18, lineHeight: '18px', textAlign: 'center',
  borderRadius: 5, flexShrink: 0, display: 'inline-block',
}
// aggregate chip (SUM / AVG …) — mono, right-aligned in the column row
const fnBadgeStyle: React.CSSProperties = {
  fontSize: 13, fontWeight: 700, fontFamily: MONO_FONT, color: 'var(--color-info)',
  background: 'var(--color-info-weak)', borderRadius: 4, padding: '1px 6px', flexShrink: 0,
}
const dragChipStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 7, fontSize: 14.5, padding: '5px 10px',
  border: '1px solid var(--color-border-strong)', borderRadius: 7,
  background: 'var(--color-raised)', color: 'var(--color-text)',
}
const overlayChipStyle: React.CSSProperties = { boxShadow: '0 8px 28px rgba(0,0,0,0.35)', cursor: 'grabbing' }
const dragHintStyle: React.CSSProperties = {
  fontSize: 14, color: 'var(--color-text-faint)', fontStyle: 'italic', marginTop: 10, padding: '8px 12px',
  background: 'var(--color-bg-secondary)', border: '1px dashed var(--color-border)', borderRadius: 7,
}
const importRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 4px', borderBottom: '1px solid var(--color-border)',
}

// joins / security extras
const joinArrowStyle: React.CSSProperties = {
  color: 'var(--color-text-faint)', fontWeight: 600, flexShrink: 0, padding: '0 2px',
}
const joinPairRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 7, marginTop: 9, flexWrap: 'wrap',
}
const joinPairLabelStyle: React.CSSProperties = {
  fontSize: 13, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
  color: 'var(--color-text-faint)', flexShrink: 0,
}
const joinEqStyle: React.CSSProperties = {
  fontFamily: MONO_FONT, fontSize: 14, fontWeight: 600, color: 'var(--color-text-faint)', flexShrink: 0,
}
const plainTermsStyle: React.CSSProperties = {
  marginTop: 9, fontSize: 14, color: 'var(--color-text-secondary)',
}
// "Add a join" panel — big setting-card radius (§7.4.3)
const addJoinPanelStyle: React.CSSProperties = {
  border: '1px solid var(--color-border)', borderRadius: 14, padding: '18px 20px', marginTop: 16,
  background: 'var(--color-bg-secondary)',
}
const settingsCardStyle: React.CSSProperties = {
  border: '1px solid var(--color-border)', borderRadius: 14, padding: '20px 22px',
  background: 'var(--color-bg-secondary)', display: 'flex', flexDirection: 'column', gap: 16,
}
